import secureStorage from '../lib/secureStorage';
import React, { useState, useEffect, useRef } from 'react';
import authedFetch from '../lib/authedFetch';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FileText, Download, Save, Edit3, Eye, Trash2, Plus, ChevronDown, ChevronUp,
  FileIcon, File, Presentation, Loader2, Check, X, RefreshCw, ArrowLeft, ArrowRight,
  AlertCircle, CheckCircle2, GripVertical, Sparkles, Wand2, Users, Building2, Compass,
  GitCompare, Star, TrendingUp, Activity, AlertTriangle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { HealthStatsStrip } from '../components/common/HealthStatsStrip';
import { InlineCreateCaseModal } from '../components/business-case/InlineCreateCaseModal';
import { ApprovalPanel, ApprovalStatusChip } from '../components/business-case/ApprovalPanel';
import { LiveRecalcPanel } from '../components/business-case/LiveRecalcPanel';
import { buildBcbHeroNarrative } from '../lib/heroNarrative';
import ProvenanceBadges from '../components/common/ProvenanceBadges';
import { readActiveProvenance } from '../lib/solutionFinderProvenance';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Section Editor Component
const SectionEditor = ({ section, onChange, onToggle, onEnhance, enhancing, enhancingId }) => {
  const [isExpanded, setIsExpanded] = useState(section.enabled);
  
  return (
    <div 
      className={`bg-white rounded-xl border transition-all ${
        section.enabled ? 'border-gray-200' : 'border-gray-100 opacity-60'
      }`}
      data-testid={`section-${section.id}`}
    >
      <div className="flex items-center gap-3 p-4 border-b border-gray-100">
        <GripVertical className="text-gray-400 cursor-move" size={20} />
        
        <button
          onClick={() => onToggle(section.id)}
          className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
            section.enabled 
              ? 'bg-blue-600 border-blue-600 text-white' 
              : 'border-gray-300 bg-white'
          }`}
        >
          {section.enabled && <Check size={14} />}
        </button>
        
        <input
          type="text"
          value={section.title}
          onChange={(e) => onChange(section.id, 'title', e.target.value)}
          className="flex-1 font-semibold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-0"
          placeholder="Section Title"
        />
        
        {/* AI Enhance Button */}
        <button
          onClick={() => onEnhance(section.id)}
          disabled={!section.enabled || enhancing}
          className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          title="Enhance with AI"
        >
          {enhancing && enhancingId === section.id ? (
            <Loader2 className="animate-spin" size={14} />
          ) : (
            <Wand2 size={14} />
          )}
          AI Enhance
        </button>
        
        {section.ai_generated && (
          <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-emerald-700 bg-emerald-50 rounded">
            <Sparkles size={12} />
            AI
          </span>
        )}
        
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
        >
          {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </button>
      </div>
      
      {isExpanded && (
        <div className="p-4">
          <textarea
            value={section.content}
            onChange={(e) => onChange(section.id, 'content', e.target.value)}
            className="w-full h-64 p-3 text-sm text-gray-700 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-y font-mono"
            placeholder="Enter section content..."
            disabled={!section.enabled}
          />
        </div>
      )}
    </div>
  );
};

// Export Format Button
const ExportButton = ({ format, icon: Icon, label, onClick, loading, loadingFormat }) => (
  <button
    onClick={() => onClick(format)}
    disabled={loading}
    className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-colors ${
      format === 'pdf' ? 'bg-rose-600 hover:bg-rose-700 text-white' :
      format === 'docx' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
      'bg-orange-600 hover:bg-orange-700 text-white'
    } disabled:opacity-50`}
    data-testid={`export-${format}-btn`}
  >
    {loading && loadingFormat === format ? (
      <Loader2 className="animate-spin" size={18} />
    ) : (
      <Icon size={18} />
    )}
    {label}
  </button>
);

// Read-only summary of the inputs that drove a saved business case.
// Renders KPI, cohort, interventions, scenario-config and the
// top-line financial outputs in a collapsible card. Includes an
// "Edit inputs" button that opens the Solution Finder pre-populated
// with this case's data.
const CaseInputsPanel = ({ solutionData, savedCaseId, title, onEdit, isVariant, onSyncFromParent }) => {
  const [open, setOpen] = useState(true);
  if (!solutionData) return null;

  const kpi = solutionData.kpi || {};
  const cohort = solutionData.cohort || null;
  const interventions = solutionData.interventions || [];
  const financial = solutionData.financial_summary || {};
  const config = solutionData.scenario_config || {};

  const fmtMoney = (v) => v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  const fmtPct = (v, d = 1) => v == null ? '—' : `${Number(v).toFixed(d)}%`;
  const fmtNum = (v) => v == null ? '—' : Number(v).toLocaleString();

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="bcb-inputs-panel">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50"
      >
        <div className="flex items-center gap-2">
          <Compass size={16} className="text-teal-600" />
          <span className="font-semibold text-slate-900 text-sm">Inputs that drove this case</span>
          {kpi.name && (
            <span className="text-xs text-slate-500">— {kpi.name}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {savedCaseId && isVariant && onSyncFromParent && (
            <button
              onClick={(e) => { e.stopPropagation(); onSyncFromParent(); }}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-amber-300 text-amber-800 text-xs font-medium rounded hover:bg-amber-50"
              data-testid="bcb-sync-from-parent-btn"
              title="Re-pull this variant's snapshot and inputs from its parent — useful when the parent was updated after the variant was forked"
            >
              <RefreshCw size={12} />
              Sync from parent
            </button>
          )}
          {savedCaseId && (
            <button
              onClick={(e) => { e.stopPropagation(); onEdit && onEdit(); }}
              className="flex items-center gap-1.5 px-2.5 py-1 bg-teal-600 text-white text-xs font-medium rounded hover:bg-teal-700"
              data-testid="bcb-edit-inputs-btn"
              title="Open this case in Solution Finder to edit its underlying values"
            >
              <Edit3 size={12} />
              Edit inputs
            </button>
          )}
          {open ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
        </div>
      </button>
      {open && (
        <div className="border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4">
          {/* KPI */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">KPI Target</div>
            <div className="text-sm font-semibold text-slate-900">{kpi.name || '—'}</div>
            <div className="text-xs text-slate-500 space-y-0.5">
              <div>Baseline: {fmtMoney(kpi.baseline_per_employee)}/employee</div>
              {financial.kpi_baseline_total != null && (
                <div>Total at risk: {fmtMoney(financial.kpi_baseline_total)}</div>
              )}
              {financial.kpi_uplift_percent != null && (
                <div>Realized uplift: {fmtPct(financial.kpi_uplift_percent, 2)}</div>
              )}
            </div>
          </div>

          {/* Cohort */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Cohort</div>
            <div className="text-sm font-semibold text-slate-900">
              {cohort?.name || 'Whole organization'}
            </div>
            <div className="text-xs text-slate-500 space-y-0.5">
              {cohort?.demographics?.population_size != null && (
                <div>Population: {fmtNum(cohort.demographics.population_size)}</div>
              )}
              {cohort?.demographics?.total_workforce != null && (
                <div>Workforce: {fmtNum(cohort.demographics.total_workforce)}</div>
              )}
            </div>
          </div>

          {/* Interventions */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">
              Interventions ({interventions.length})
            </div>
            <div className="text-xs text-slate-700 space-y-1">
              {interventions.length === 0 && <span className="italic text-slate-400">None</span>}
              {interventions.slice(0, 4).map((iv, i) => (
                <div key={i} className="flex items-baseline gap-1.5">
                  <span className="font-medium text-slate-800 truncate">{iv.name || iv.intervention_id || `Intervention ${i + 1}`}</span>
                  <span className="text-slate-400 text-[10px] ml-auto whitespace-nowrap">
                    {iv.modality_id || ''}{iv.target_audience_size ? ` · ${fmtNum(iv.target_audience_size)} ppl` : ''}
                  </span>
                </div>
              ))}
              {interventions.length > 4 && (
                <div className="text-[10px] text-slate-400">+ {interventions.length - 4} more</div>
              )}
            </div>
          </div>

          {/* Scenario Config + Investment */}
          <div className="space-y-1">
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Modelling</div>
            <div className="text-xs text-slate-500 space-y-0.5">
              <div>Horizon: {config.time_horizon_months ?? 12} months</div>
              <div>Lag: {config.implementation_lag_months ?? 0}m · Ramp: {config.ramp_up_months ?? 0}m</div>
              {config.include_decay && (
                <div>Decay: {fmtPct((config.annual_decay_rate || 0) * 100, 0)}/yr</div>
              )}
            </div>
            <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500 pt-1">Investment</div>
            <div className="text-sm font-semibold text-slate-900">{fmtMoney(financial.total_investment)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// Side-by-side compare view for a parent case and its variants.
// Renders: title row, ROE snapshot deltas vs parent, then a merged
// sections list where each cell shows word-count and an enabled/
// disabled dot. Designed to sit inside a wide Dialog.
const CompareVariantsView = ({ parent, variants, onPromote, promoting, onTogglePreferred }) => {
  const columns = [parent, ...variants];
  // Union of all section ids across columns, preserving parent order first
  const sectionOrder = [];
  const sectionTitleMap = {};
  columns.forEach(c => {
    (c.sections || []).forEach(s => {
      if (!(s.id in sectionTitleMap)) {
        sectionOrder.push(s.id);
        sectionTitleMap[s.id] = s.title;
      }
    });
  });

  const wordCount = (text) => (text || '').trim().split(/\s+/).filter(Boolean).length;
  const roeMetric = (col, key) => col?.roe_snapshot?.[key];
  const fmtDelta = (variantVal, parentVal) => {
    if (variantVal == null || parentVal == null) return null;
    const d = Number(variantVal) - Number(parentVal);
    if (!Number.isFinite(d) || Math.abs(d) < 0.05) return null;
    const sign = d > 0 ? '+' : '';
    const cls = d > 0 ? 'text-emerald-600' : 'text-rose-600';
    return <span className={`text-[10px] font-semibold ${cls} ml-1`}>{sign}{d.toFixed(1)}</span>;
  };

  const metrics = [
    { key: 'pe_ideal_pct', label: 'PE Ideal', color: 'text-purple-600' },
    { key: 'roe_pct', label: 'ROE', color: 'text-teal-600' },
    { key: 'pe_validated_pct', label: 'PE Validated', color: 'text-purple-700' },
    { key: 'transfer_gap_pct', label: 'Gap', color: 'text-amber-600' },
  ];

  return (
    <div className="min-w-full">
      <table className="w-full border-collapse" data-testid="bcb-compare-table">
        <thead>
          <tr className="text-left">
            <th className="sticky left-0 bg-white z-10 p-3 border-b border-slate-200 text-xs font-semibold uppercase tracking-wide text-slate-500 min-w-[160px]">Attribute</th>
            {columns.map((c, idx) => (
              <th key={c.id} className={`p-3 border-b-2 ${idx === 0 ? 'border-slate-400' : 'border-amber-400'} min-w-[220px]`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${idx === 0 ? 'bg-slate-100 text-slate-700' : 'bg-amber-100 text-amber-800'}`}>
                    {idx === 0 ? 'Parent' : `Variant ${idx}`}
                  </span>
                  <div className="flex items-center gap-1">
                    {onTogglePreferred && (
                      <button
                        onClick={() => onTogglePreferred(c.id)}
                        className={`p-1 rounded transition-colors ${c.is_preferred ? 'text-amber-500 hover:text-amber-600' : 'text-slate-300 hover:text-amber-500'}`}
                        title={c.is_preferred ? 'Preferred scenario — click to unmark' : 'Bookmark as preferred scenario'}
                        data-testid={`bcb-compare-preferred-${c.id}`}
                      >
                        <Star size={13} fill={c.is_preferred ? 'currentColor' : 'none'} />
                      </button>
                    )}
                    {idx > 0 && onPromote && (
                      <button
                        onClick={() => onPromote(c.id)}
                        disabled={promoting === c.id}
                        className="flex items-center gap-1 px-2 py-1 text-[10px] font-semibold text-white bg-teal-600 hover:bg-teal-700 rounded disabled:opacity-50"
                        title="Make this variant the new parent of the case family"
                        data-testid={`bcb-compare-promote-${c.id}`}
                      >
                        {promoting === c.id ? (
                          <Loader2 className="animate-spin" size={11} />
                        ) : (
                          <ArrowLeft className="rotate-180" size={11} />
                        )}
                        Promote → Parent
                      </button>
                    )}
                  </div>
                </div>
                <div className="text-sm font-semibold text-slate-900 mt-1 truncate" title={c.title}>{c.title}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ''}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* Description row */}
          <tr>
            <td className="sticky left-0 bg-white p-3 align-top border-b border-slate-100 text-xs font-medium text-slate-600">Description</td>
            {columns.map(c => (
              <td key={`${c.id}-desc`} className="p-3 align-top border-b border-slate-100 text-xs text-slate-600">
                {c.description || <span className="italic text-slate-300">—</span>}
              </td>
            ))}
          </tr>

          {/* ROE snapshot rows */}
          {metrics.map(m => {
            const parentVal = roeMetric(parent, m.key);
            const anyHasValue = columns.some(c => roeMetric(c, m.key) != null);
            if (!anyHasValue) return null;
            return (
              <tr key={m.key}>
                <td className="sticky left-0 bg-white p-3 align-top border-b border-slate-100 text-xs font-medium text-slate-600">{m.label}</td>
                {columns.map((c, idx) => {
                  const v = roeMetric(c, m.key);
                  return (
                    <td key={`${c.id}-${m.key}`} className="p-3 align-top border-b border-slate-100">
                      {v == null ? (
                        <span className="text-xs italic text-slate-300">—</span>
                      ) : (
                        <span className={`text-sm font-bold ${m.color}`}>
                          {Number(v).toFixed(1)}%
                          {idx > 0 && fmtDelta(v, parentVal)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}

          {/* Inputs (only rows that differ across columns) */}
          {(() => {
            const inputRows = [
              { key: 'kpi.name', label: 'KPI', extract: (sd) => sd?.kpi?.name || null },
              { key: 'cohort.name', label: 'Cohort', extract: (sd) => sd?.cohort?.name || null },
              { key: 'cohort.population', label: 'Population', extract: (sd) => sd?.cohort?.demographics?.population_size ?? null, format: (v) => v == null ? null : Number(v).toLocaleString() },
              { key: 'investment', label: 'Investment', extract: (sd) => sd?.financial_summary?.total_investment ?? null, format: (v) => v == null ? null : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}` },
              { key: 'horizon', label: 'Horizon', extract: (sd) => sd?.scenario_config?.time_horizon_months ?? null, format: (v) => v == null ? null : `${v} mo` },
              { key: 'lag', label: 'Implementation lag', extract: (sd) => sd?.scenario_config?.implementation_lag_months ?? null, format: (v) => v == null ? null : `${v} mo` },
              { key: 'ramp', label: 'Ramp-up', extract: (sd) => sd?.scenario_config?.ramp_up_months ?? null, format: (v) => v == null ? null : `${v} mo` },
              { key: 'interventions_count', label: 'Interventions', extract: (sd) => Array.isArray(sd?.interventions) ? sd.interventions.length : null },
            ];
            const visibleRows = inputRows
              .map(r => ({ ...r, values: columns.map(c => r.extract(c.solution_data)) }))
              .filter(r => {
                // Only show when at least one variant differs from the parent
                const parentVal = r.values[0];
                return r.values.slice(1).some(v => v !== parentVal);
              });
            if (visibleRows.length === 0) return null;
            return (
              <>
                <tr className="bg-slate-50">
                  <td colSpan={columns.length + 1} className="p-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Inputs that differ
                  </td>
                </tr>
                {visibleRows.map(r => (
                  <tr key={r.key}>
                    <td className="sticky left-0 bg-white p-3 align-top border-b border-slate-100 text-xs font-medium text-slate-600">{r.label}</td>
                    {r.values.map((v, idx) => {
                      const parentVal = r.values[0];
                      const differs = idx > 0 && v !== parentVal;
                      const display = v == null
                        ? <span className="italic text-slate-300">—</span>
                        : (r.format ? r.format(v) : String(v));
                      return (
                        <td key={`${r.key}-${idx}`} className={`p-3 align-top border-b border-slate-100 text-xs ${differs ? 'bg-amber-50 font-semibold text-slate-900' : 'text-slate-600'}`}>
                          {display}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </>
            );
          })()}

          {/* Sections header */}
          <tr className="bg-slate-50">
            <td colSpan={columns.length + 1} className="p-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Sections ({sectionOrder.length})
            </td>
          </tr>

          {/* Section rows */}
          {sectionOrder.map(sid => {
            const parentSection = (parent.sections || []).find(s => s.id === sid);
            const parentWords = parentSection ? wordCount(parentSection.content) : 0;
            return (
              <tr key={sid}>
                <td className="sticky left-0 bg-white p-3 align-top border-b border-slate-100 text-xs font-medium text-slate-700">
                  {sectionTitleMap[sid]}
                </td>
                {columns.map((c, idx) => {
                  const s = (c.sections || []).find(x => x.id === sid);
                  if (!s) {
                    return (
                      <td key={`${c.id}-${sid}`} className="p-3 align-top border-b border-slate-100 text-xs italic text-slate-300">
                        not present
                      </td>
                    );
                  }
                  const words = wordCount(s.content);
                  const delta = idx > 0 ? words - parentWords : 0;
                  return (
                    <td key={`${c.id}-${sid}`} className="p-3 align-top border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${s.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`} title={s.enabled ? 'Enabled' : 'Disabled'} />
                        <span className="text-xs text-slate-600">{words} words</span>
                        {idx > 0 && delta !== 0 && (
                          <span className={`text-[10px] font-semibold ${delta > 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                            {delta > 0 ? '+' : ''}{delta}
                          </span>
                        )}
                      </div>
                      {s.content && (
                        <p className="text-[11px] text-slate-500 mt-1 line-clamp-3">{s.content}</p>
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// Main Business Case Builder Page
const BusinessCaseBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isSuperAdmin, viewAsOrg } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [enhancing, setEnhancing] = useState(false);
  const [enhancingId, setEnhancingId] = useState(null);
  const [generatingAll, setGeneratingAll] = useState(false);
  const [tone, setTone] = useState('professional');
  const [organizations, setOrganizations] = useState([]);
  // Default org scope to the user's RBAC-scoped tenant (viewAsOrg).
  // Super-Admins can switch to other orgs they're permitted to see;
  // tenant users only see their own org and can never override.
  const [selectedOrg, setSelectedOrg] = useState(viewAsOrg?.id || 'all');
  // Recent vs. All-history toggle for the cases grid + free-text filter.
  const [casesView, setCasesView] = useState('recent'); // 'recent' | 'all'
  const [casesFilter, setCasesFilter] = useState('');
  // Provenance source filter — chip-row above the grid that lets
  // users slice the library by where the case was modelled from.
  // null = All, 'constraint' / 'nav_condition' / 'pe_benchmarking' /
  // 'industry_kpi' / 'direct' (no provenance dict at all).
  const [provenanceFilter, setProvenanceFilter] = useState(null);
  // Cross-tenant panel drill-down — when a Super Admin clicks a
  // source tile in the cross-tenant panel, expand a small drawer
  // showing the 5 most recent cases from that source plus a
  // "biggest swing" callout identifying which case is dragging the
  // average up or pulling the trend down.
  const [crossTenantDrillSource, setCrossTenantDrillSource] = useState(null);
  
  const [title, setTitle] = useState('Business Case for Learning Investment');
  const [sections, setSections] = useState([]);
  const [solutionData, setSolutionData] = useState(null);
  const [savedCaseId, setSavedCaseId] = useState(null);
  const [savedCases, setSavedCases] = useState([]);
  const [currency, setCurrency] = useState('USD');
  // Compare-variants modal state: when the user clicks the
  // GitCompare button on a parent case, we fetch the parent + its
  // children from the backend and render a side-by-side diff.
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [promotingId, setPromotingId] = useState(null);
  // Default-on opt-in for the CFO Recommendation cover slide. Only
  // surfaced in the UI when the loaded case belongs to a family that
  // has at least one bookmarked variant.
  const [includeCfoCover, setIncludeCfoCover] = useState(true);
  // Current org-level health snapshot for the starter tile.
  // Pulls live LEI / PE / ROE / PE-Validated from the same source
  // the Home dashboard uses (`/health/quarter-deltas`).
  const [healthSnapshot, setHealthSnapshot] = useState(null);
  // Sync-from-parent confirm dialog state.
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncCommitting, setSyncCommitting] = useState(false);
  const [roeContext, setRoeContext] = useState(null);
  // Industry benchmark "competitive urgency" stamp — populated by
  // /generate-sections + /generate-ai-sections when the org is below
  // the industry median for this KPI. Null otherwise.
  const [benchmarkContext, setBenchmarkContext] = useState(null);
  // Frozen benchmark snapshot from the last save — passed back to the
  // backend on regenerate so it can detect drift ("you've slipped
  // from 58th → 42nd percentile since this case was last saved").
  const [priorBenchmarkSnapshot, setPriorBenchmarkSnapshot] = useState(null);
  // Drift dict returned by backend when prior vs current percentile
  // differ materially (≥5 points or tier flip). Null when no drift.
  const [benchmarkDrift, setBenchmarkDrift] = useState(null);
  // Narrative-bridge provenance carried over from Solution Finder via
  // location.state. Used at save time so each saved business case
  // records "From Mission Control · {constraint}", "From Org Benchmarks
  // · {nav-condition}", and "From Industry Benchmarks · {kpi}" badges.
  const [currentProvenance, setCurrentProvenance] = useState(
    () => location.state?.provenance || location.state?.solutionData?.provenance || null,
  );
  // Inline-create modal — lets the user spin up a fully calculated
  // business case without leaving BCB.
  const [inlineCreateOpen, setInlineCreateOpen] = useState(false);
  // Approval/sign-off state mirroring fields persisted on the loaded
  // case (status, approver_email, submitted_at, approved_by, etc.).
  // Refreshed whenever a case is loaded OR the ApprovalPanel fires a
  // state change. Also powers the status chip in the editor header.
  const [approvalData, setApprovalData] = useState(null);
  // Modality catalog — fetched once, handed to LiveRecalcPanel.
  const [modalitiesCatalog, setModalitiesCatalog] = useState([]);
  
  // Initialize from location state (from Solution Finder) or load saved cases
  useEffect(() => {
    if (isSuperAdmin) {
      authedFetch(`${API_URL}/api/rbac/organizations`, {
        headers: { 'Authorization': `Bearer ${secureStorage.getItem('session_token')}` }
      }).then(r => r.json()).then(d => setOrganizations(d.organizations || [])).catch(() => {});
    }
  }, [isSuperAdmin]);

  // When the global "View As Org" changes (driven by the topbar),
  // re-scope the local filter so cases reflect the active tenant.
  useEffect(() => {
    if (viewAsOrg?.id) setSelectedOrg(viewAsOrg.id);
  }, [viewAsOrg?.id]);

  // Fetch the modality catalog once — LiveRecalcPanel needs it to
  // render the modality dropdown per intervention.
  useEffect(() => {
    (async () => {
      try {
        const r = await authedFetch(`${API_URL}/api/solution-finder/modalities`);
        if (r.ok) {
          const d = await r.json();
          setModalitiesCatalog(d.modalities || []);
        }
      } catch { /* non-fatal */ }
    })();
  }, []);

  useEffect(() => {
    const initializeData = async () => {
      setLoading(true);
      const token = secureStorage.getItem('session_token');
      
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Check if coming from Solution Finder with data
      if (location.state?.solutionData) {
        const data = location.state.solutionData;
        setSolutionData(data);
        setCurrency(location.state.currency || 'USD');

        // Round-trip from "Edit inputs": link the new data back to
        // the original saved case so Save = update, Save-as-variant
        // = branch off the original.
        if (location.state.originalCaseId) {
          setSavedCaseId(location.state.originalCaseId);
          if (location.state.originalCaseTitle) setTitle(location.state.originalCaseTitle);
          setSuccess('Inputs updated from Solution Finder. Click Update Business Case to save, or "Save as variant" to keep the original intact.');
          setTimeout(() => setSuccess(null), 8000);
        }
        
        // Generate sections from solution data
        try {
          const response = await authedFetch(`${API_URL}/api/business-case-builder/generate-sections`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              ...data,
              // Carry the round-tripped case's prior benchmark snapshot
              // (when present in location.state) so the backend can
              // detect competitive drift on this regeneration.
              prior_benchmark_context: location.state.priorBenchmarkSnapshot || data.prior_benchmark_context || null,
              currency: location.state.currency || 'USD'
            })
          });
          
          if (response.ok) {
            const result = await response.json();
            setSections(result.sections);
            if (result.roe_context) setRoeContext(result.roe_context);
            if (result.benchmark_context !== undefined) setBenchmarkContext(result.benchmark_context);
            if (result.benchmark_drift !== undefined) setBenchmarkDrift(result.benchmark_drift);
            
            // Set title based on KPI
            const kpiName = data.kpi?.name || 'Learning Investment';
            // Don't overwrite a round-trip title if one was provided
            if (!location.state.originalCaseTitle) {
              setTitle(`Business Case: ${kpiName}`);
            }
          }
        } catch (err) {
          logger.error('Error generating sections:', err);
          setError('Failed to generate business case sections');
        }
      }
      
      // Load saved cases list
      try {
        const orgParam = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
        const response = await authedFetch(`${API_URL}/api/business-case-builder/list${orgParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const result = await response.json();
          setSavedCases(result.cases || []);
        }
      } catch (err) {
        logger.error('Error loading saved cases:', err);
      }
      
      // Fetch ROE context if not already loaded from section generation
      if (!roeContext) {
        try {
          const orgParam = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
          const roeRes = await authedFetch(`${API_URL}/api/business-case-builder/roe-context${orgParam}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (roeRes.ok) {
            const roeData = await roeRes.json();
            setRoeContext(roeData);
          }
        } catch (err) {
          logger.error('Error fetching ROE context:', err);
        }
      }

      // Fetch live health stats for the starter tile (LEI / PE / ROE / KPI = PE Validated)
      try {
        const orgParam = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
        const hRes = await authedFetch(`${API_URL}/api/health/quarter-deltas${orgParam}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (hRes.ok) {
          const hData = await hRes.json();
          setHealthSnapshot(hData);
        }
      } catch (err) {
        logger.error('Error fetching health snapshot:', err);
      }
      
      setLoading(false);
    };
    
    initializeData();
  }, [navigate, location.state, selectedOrg]);
  
  // Handle section change
  const handleSectionChange = (sectionId, field, value) => {
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, [field]: value } : s
    ));
  };
  
  // Toggle section enabled
  const handleToggleSection = (sectionId) => {
    setSections(prev => prev.map(s => 
      s.id === sectionId ? { ...s, enabled: !s.enabled } : s
    ));
  };
  
  // Enhance single section with AI
  const handleEnhanceSection = async (sectionId) => {
    if (!solutionData) {
      setError('Solution data required for AI enhancement');
      return;
    }
    
    const section = sections.find(s => s.id === sectionId);
    if (!section) return;
    
    setEnhancing(true);
    setEnhancingId(sectionId);
    setError(null);
    
    const token = secureStorage.getItem('session_token');
    
    try {
      const response = await authedFetch(`${API_URL}/api/business-case-builder/enhance-section`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          section_id: sectionId,
          section_title: section.title,
          current_content: section.content,
          solution_data: solutionData,
          tone
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setSections(prev => prev.map(s => 
          s.id === sectionId ? { ...s, content: result.content, ai_generated: true } : s
        ));
        setSuccess(`"${section.title}" enhanced with AI`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Failed to enhance section');
      }
    } catch (err) {
      setError('Failed to enhance section with AI');
    }
    
    setEnhancing(false);
    setEnhancingId(null);
  };
  
  // Generate all sections with AI
  const handleGenerateAllWithAI = async () => {
    if (!solutionData) {
      setError('Solution data required for AI generation');
      return;
    }
    
    setGeneratingAll(true);
    setError(null);
    
    const token = secureStorage.getItem('session_token');
    
    try {
      const response = await authedFetch(`${API_URL}/api/business-case-builder/generate-ai-sections`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          solution_data: {
            ...solutionData,
            // Carry the prior snapshot so backend can detect drift on
            // regenerate (e.g., user clicks "Generate all with AI"
            // again after new actuals landed in the org).
            prior_benchmark_context: priorBenchmarkSnapshot || null,
          },
          tone,
          currency
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        setSections(result.sections);
        if (result.roe_context) setRoeContext(result.roe_context);
        if (result.benchmark_context !== undefined) setBenchmarkContext(result.benchmark_context);
        if (result.benchmark_drift !== undefined) setBenchmarkDrift(result.benchmark_drift);
        setSuccess('All sections generated with AI');
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Failed to generate sections');
      }
    } catch (err) {
      setError('Failed to generate AI sections');
    }
    
    setGeneratingAll(false);
  };
  
  // Save business case (default: update-in-place for existing; insert for new)
  // `options.asVariant=true` forces a fresh insert with parent_case_id,
  // regardless of whether a savedCaseId is currently loaded.
  const handleSave = async (options = {}) => {
    const { asVariant = false } = options;
    setSaving(true);
    setError(null);
    
    const token = secureStorage.getItem('session_token');
    
    try {
      const isVariant = asVariant && savedCaseId;
      const endpoint = (savedCaseId && !isVariant)
        ? `${API_URL}/api/business-case-builder/${savedCaseId}`
        : `${API_URL}/api/business-case-builder/save`;
      const method = (savedCaseId && !isVariant) ? 'PUT' : 'POST';
      
      const body = {
        title: isVariant ? `${title} (variant)` : title,
        description: `Business case for ${solutionData?.kpi?.name || 'learning investment'}`,
        sections,
        solution_data: solutionData,
      };
      if (isVariant) body.parent_case_id = savedCaseId;
      if (roeContext?.has_data) body.roe_snapshot = roeContext;
      // Freeze the current benchmark context on this case so the next
      // regenerate can detect drift ("you've slipped 16 points since
      // this case was last saved"). Null is allowed — the backend
      // treats it as no-snapshot.
      if (benchmarkContext) body.benchmark_snapshot = benchmarkContext;
      // Narrative-bridge provenance — prefer the snapshot carried
      // over from Solution Finder via location.state (captured at the
      // time the user clicked "Export Business Case"), and fall back
      // to whatever URL params happen to be active right now.
      const prov = currentProvenance || readActiveProvenance({ kpis: [] });
      if (prov) body.provenance = prov;

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        const result = await response.json();
        if (isVariant || !savedCaseId) {
          setSavedCaseId(result.id);
          if (isVariant) setTitle(body.title);
        }
        // The case now has the current benchmark context as its
        // baseline snapshot — clear any active drift, and freeze the
        // current context as the next-comparison baseline.
        if (benchmarkContext) setPriorBenchmarkSnapshot(benchmarkContext);
        setBenchmarkDrift(null);
        setSuccess(isVariant ? 'Variant saved successfully' : 'Business case saved successfully');
        setTimeout(() => setSuccess(null), 3000);
        
        // Refresh saved cases list
        const orgP = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
        const listResponse = await authedFetch(`${API_URL}/api/business-case-builder/list${orgP}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (listResponse.ok) {
          const listResult = await listResponse.json();
          setSavedCases(listResult.cases || []);
        }
      } else {
        throw new Error('Failed to save');
      }
    } catch (err) {
      setError(asVariant ? 'Failed to save variant' : 'Failed to save business case');
    }
    
    setSaving(false);
  };
  
  // Load saved case
  const handleLoadCase = async (caseId) => {
    setLoading(true);
    const token = secureStorage.getItem('session_token');
    
    try {
      const response = await authedFetch(`${API_URL}/api/business-case-builder/${caseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setTitle(result.title);
        setSections(result.sections);
        setSolutionData(result.solution_data);
        setSavedCaseId(caseId);
        // Capture the frozen benchmark snapshot (if any) so the next
        // regenerate hands it back to the backend for drift detection.
        // Also seed the in-banner context so it shows immediately.
        if (result.benchmark_snapshot) {
          setPriorBenchmarkSnapshot(result.benchmark_snapshot);
          setBenchmarkContext(result.benchmark_snapshot);
        } else {
          setPriorBenchmarkSnapshot(null);
          setBenchmarkContext(null);
        }
        setBenchmarkDrift(null);
        // Preserve narrative-bridge provenance across reloads so the
        // case's existing badges + future re-saves remain consistent.
        setCurrentProvenance(result.provenance || null);
        setApprovalData({
          status: result.status || 'draft',
          approver_email: result.approver_email,
          submitted_at: result.submitted_at,
          submitted_by_email: result.submitted_by_email,
          approved_at: result.approved_at,
          approved_by: result.approved_by,
          rejected_at: result.rejected_at,
          rejected_by: result.rejected_by,
          approval_history: result.approval_history || [],
        });
      }
    } catch (err) {
      setError('Failed to load business case');
    }
    
    setLoading(false);
  };

  // Re-fetch just the approval metadata for the currently-loaded
  // case. Called after ApprovalPanel mutations so the chip + panel
  // reflect the new status without a full case reload.
  const refreshApprovalData = async () => {
    if (!savedCaseId) return;
    const token = secureStorage.getItem('session_token');
    try {
      const r = await authedFetch(`${API_URL}/api/business-case-builder/${savedCaseId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) return;
      const result = await r.json();
      setApprovalData({
        status: result.status || 'draft',
        approver_email: result.approver_email,
        submitted_at: result.submitted_at,
        submitted_by_email: result.submitted_by_email,
        approved_at: result.approved_at,
        approved_by: result.approved_by,
        rejected_at: result.rejected_at,
        rejected_by: result.rejected_by,
        approval_history: result.approval_history || [],
      });
      // Also refresh the list so the grid chip updates
      const orgP = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const listRes = await authedFetch(`${API_URL}/api/business-case-builder/list${orgP}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (listRes.ok) setSavedCases((await listRes.json()).cases || []);
    } catch { /* non-fatal */ }
  };
  
  // Delete saved case
  const handleDeleteCase = async (caseId, e) => {
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this business case?')) {
      return;
    }
    
    const token = secureStorage.getItem('session_token');
    
    try {
      const response = await authedFetch(`${API_URL}/api/business-case-builder/${caseId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setSavedCases(prev => prev.filter(c => c.id !== caseId));
        if (savedCaseId === caseId) {
          setSavedCaseId(null);
          setSections([]);
          setTitle('Business Case for Learning Investment');
        }
      }
    } catch (err) {
      setError('Failed to delete business case');
    }
  };

  // Open the Compare Variants modal for a parent case.
  // Fetches parent + children in a single call and renders them
  // side-by-side with deltas against the parent.
  const handleOpenCompare = async (parentId) => {
    setCompareOpen(true);
    setCompareLoading(true);
    setCompareData(null);
    const token = secureStorage.getItem('session_token');
    try {
      const res = await authedFetch(`${API_URL}/api/business-case-builder/${parentId}/variants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setCompareData(await res.json());
      } else {
        setError('Failed to load variants for comparison');
        setCompareOpen(false);
      }
    } catch (err) {
      setError('Failed to load variants for comparison');
      setCompareOpen(false);
    }
    setCompareLoading(false);
  };

  // Promote a variant to be the new parent. Backend re-points all
  // siblings + demotes the former parent to a sibling. After it
  // finishes we re-fetch the variants list so the modal stays open
  // with the new parent on the left.
  const handlePromoteVariant = async (variantId) => {
    setPromotingId(variantId);
    const token = secureStorage.getItem('session_token');
    try {
      const res = await authedFetch(`${API_URL}/api/business-case-builder/${variantId}/promote-to-parent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Promote failed');

      // Refresh the cases grid in the background
      const orgP = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      authedFetch(`${API_URL}/api/business-case-builder/list${orgP}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      }).then(r => r.ok ? r.json() : null).then(d => { if (d) setSavedCases(d.cases || []); }).catch(() => {});

      // Reload the comparison from the new parent so the modal updates in place
      const variantsRes = await authedFetch(`${API_URL}/api/business-case-builder/${variantId}/variants`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (variantsRes.ok) {
        setCompareData(await variantsRes.json());
      }
      setSuccess('Variant promoted to parent');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to promote variant');
    }
    setPromotingId(null);
  };

  // Re-pull the parent's current snapshot/solution_data into this
  // variant. Two-step flow: open the diff preview dialog first, then
  // commit on user confirmation. Sections + title are preserved.
  const handleSyncFromParent = async () => {
    if (!savedCaseId) return;
    setSyncDialogOpen(true);
    setSyncPreview(null);
    const token = secureStorage.getItem('session_token');
    try {
      const res = await authedFetch(`${API_URL}/api/business-case-builder/${savedCaseId}/sync-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || 'Sync preview failed');
      }
      setSyncPreview(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to load sync preview');
      setSyncDialogOpen(false);
    }
  };

  const handleConfirmSync = async () => {
    if (!savedCaseId) return;
    setSyncCommitting(true);
    const token = secureStorage.getItem('session_token');
    try {
      const res = await authedFetch(`${API_URL}/api/business-case-builder/${savedCaseId}/sync-from-parent`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail?.detail || 'Sync failed');
      }
      await handleLoadCase(savedCaseId);
      const orgP = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const listRes = await authedFetch(`${API_URL}/api/business-case-builder/list${orgP}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (listRes.ok) setSavedCases((await listRes.json()).cases || []);
      setSuccess('Inputs synced from parent');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message || 'Failed to sync from parent');
    }
    setSyncCommitting(false);
    setSyncDialogOpen(false);
    setSyncPreview(null);
  };

  // Open the loaded case in Solution Finder for input editing.
  // The Finder consumes `prefillFromCase` from location.state and
  // seeds its KPI/cohort/intervention/scenario state. After recalc,
  // the Finder navigates back to BCB with `originalCaseId` so the
  // saved case is updated rather than duplicated.
  const handleEditInputs = () => {
    if (!solutionData) return;
    navigate('/solution-finder', {
      state: {
        prefillFromCase: {
          caseId: savedCaseId || null,
          caseTitle: title,
          kpi: solutionData.kpi || null,
          interventions: solutionData.interventions || [],
          cohort: solutionData.cohort || null,
          scenario_config: solutionData.scenario_config || null,
        }
      }
    });
  };

  // Toggle the `is_preferred` flag on a case. Backend ensures only
  // one preferred per family. We optimistically refresh both the
  // saved-cases grid and the open compare dialog (if any).
  const handleTogglePreferred = async (caseId) => {
    const token = secureStorage.getItem('session_token');
    try {
      const res = await authedFetch(`${API_URL}/api/business-case-builder/${caseId}/toggle-preferred`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error();

      // Refresh saved cases grid
      const orgP = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
      const listRes = await authedFetch(`${API_URL}/api/business-case-builder/list${orgP}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (listRes.ok) setSavedCases((await listRes.json()).cases || []);

      // Refresh open compare dialog
      if (compareOpen && compareData?.parent?.id) {
        const vRes = await authedFetch(`${API_URL}/api/business-case-builder/${compareData.parent.id}/variants`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (vRes.ok) setCompareData(await vRes.json());
      }
    } catch (err) {
      setError('Failed to update preferred flag');
    }
  };
  
  // Export business case
  const handleExport = async (format) => {
    setExporting(true);
    setExportFormat(format);
    setError(null);
    
    const token = secureStorage.getItem('session_token');
    
    try {
      const response = await authedFetch(`${API_URL}/api/business-case-builder/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: savedCaseId || null,
          title,
          sections,
          solution_data: solutionData,
          cohort: solutionData?.cohort || null,
          format,
          currency,
          include_logo: true,
          include_cfo_cover: includeCfoCover
        })
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `business_case_${new Date().toISOString().split('T')[0]}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Track export
        authedFetch(`${API_URL}/api/solution-finder/track-event`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            event_type: 'business_case_exported',
            metadata: {
              format,
              sections_count: sections.filter(s => s.enabled).length,
              has_solution_data: !!solutionData
            }
          })
        }).catch(() => {});
        
        setSuccess(`Business case exported as ${format.toUpperCase()}`);
        setTimeout(() => setSuccess(null), 3000);
      } else {
        throw new Error('Export failed');
      }
    } catch (err) {
      setError(`Failed to export as ${format.toUpperCase()}`);
    }
    
    setExporting(false);
    setExportFormat(null);
  };

  // ──────────────────────────────────────────────────────────────
  // Hero-narrative bandit + analytics hooks
  // ──────────────────────────────────────────────────────────────
  // These MUST live above any early-return below (rules-of-hooks).
  // Compute the narrative once here and reuse downstream so the
  // hooks have a stable derivation order on every render.
  const __heroNarrativeForHooks = buildBcbHeroNarrative({ healthSnapshot, roeContext });
  const [__heroVariant, setHeroVariant] = useState(null); // { variant_id, suffix }
  const __pickedKeyRef = useRef(null);
  useEffect(() => {
    if (!healthSnapshot) return;
    const key = `${__heroNarrativeForHooks.kind}::${__heroNarrativeForHooks.metricKey || ''}`;
    if (__pickedKeyRef.current === key) return;
    __pickedKeyRef.current = key;
    const token = secureStorage.getItem('session_token');
    if (!token) return;
    (async () => {
      try {
        const res = await authedFetch(
          `${API_URL}/api/business-case-builder/analytics/hero-narrative-pick`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              kind: __heroNarrativeForHooks.kind,
              metric_key: __heroNarrativeForHooks.metricKey,
            }),
          }
        );
        if (res.ok) {
          const d = await res.json();
          setHeroVariant({ variant_id: d.variant_id, suffix: d.suffix });
        }
      } catch { /* fall back silently */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthSnapshot, __heroNarrativeForHooks.kind, __heroNarrativeForHooks.metricKey]);

  const __activeVariantIdForHooks = __heroVariant?.variant_id || null;
  const __viewedKeyRef = useRef(null);
  useEffect(() => {
    if (!healthSnapshot) return;
    if (__heroNarrativeForHooks.kind !== 'fallback' && !__activeVariantIdForHooks) return;
    const key = `${__heroNarrativeForHooks.kind}::${__heroNarrativeForHooks.metricKey || ''}::${__activeVariantIdForHooks || ''}`;
    if (__viewedKeyRef.current === key) return;
    __viewedKeyRef.current = key;
    const token = secureStorage.getItem('session_token');
    if (!token) return;
    const renderedHeadline = __heroVariant?.suffix
      ? `${__heroNarrativeForHooks.prefix} — ${__heroVariant.suffix}`
      : __heroNarrativeForHooks.headline;
    void authedFetch(`${API_URL}/api/business-case-builder/analytics/hero-narrative-event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        event: 'viewed',
        kind: __heroNarrativeForHooks.kind,
        metric_key: __heroNarrativeForHooks.metricKey,
        headline: renderedHeadline,
        delta: __heroNarrativeForHooks.deltaText
          ? parseFloat(__heroNarrativeForHooks.deltaText)
          : null,
        variant_id: __activeVariantIdForHooks,
      }),
    }).catch(() => { /* analytics is best-effort */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [healthSnapshot, __heroNarrativeForHooks.kind, __heroNarrativeForHooks.metricKey, __activeVariantIdForHooks]);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-blue-600" size={48} />
      </div>
    );
  }
  
  // Theme aligned with Solution Finder (Navy) — these two pages
  // are functionally a pair (model → export) so they should
  // visually read as one journey, not two distinct sections.
  const __pageTheme = getLightSectionTheme('Navy');
  // Map of parentId -> variant count. Only parents that actually
  // have saved children render the "Compare variants" button.
  const variantCountByParent = savedCases.reduce((acc, c) => {
    if (c.parent_case_id) {
      acc[c.parent_case_id] = (acc[c.parent_case_id] || 0) + 1;
    }
    return acc;
  }, {});

  // Whether the currently-loaded case belongs to a family that has at
  // least one bookmarked (preferred) member. Drives visibility of the
  // "Include CFO Recommendation cover" checkbox in the export bar.
  const familyHasPreferredBookmark = (() => {
    if (!savedCaseId) return false;
    const me = savedCases.find(c => c.id === savedCaseId);
    if (!me) return false;
    const familyParentId = me.parent_case_id || me.id;
    return savedCases.some(c =>
      c.is_preferred && (c.id === familyParentId || c.parent_case_id === familyParentId)
    );
  })();
  const bcbHeroActions = (
    <button
      onClick={() => navigate(-1)}
      className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 text-slate-900 rounded-lg hover:bg-white"
      data-testid="bcb-back-btn"
    >
      <ArrowLeft size={18} />
      Back
    </button>
  );

  // Hero panel — replaces the prior 3 stat tiles with a dedicated
  // "Start a new case" CTA panel. Keeps the live LEI/PE/ROE strip
  // visible on entry so users see org-health + start-new in one
  // glance, separated from the saved-case grid below (where users
  // review/compare existing cases).
  // (Hook-based work — variant pick, viewed logging — happens above
  // the `if (loading)` early return per rules-of-hooks. We just
  // reuse those derived values here.)
  const __heroNarrative = __heroNarrativeForHooks;
  const __narrativeAccent = __heroNarrative.kind === 'decline'
    ? 'bg-rose-50 text-rose-700 border-rose-200'
    : __heroNarrative.kind === 'gain'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : null;
  const __renderedHeadline = __heroVariant?.suffix
    ? `${__heroNarrative.prefix} — ${__heroVariant.suffix}`
    : __heroNarrative.headline;
  const __activeVariantId = __activeVariantIdForHooks;

  const handleHeroClick = () => {
    // Fire the click event before opening the modal so we don't lose
    // the impression if the user closes the modal abruptly.
    try {
      const token = secureStorage.getItem('session_token');
      if (token) {
        void authedFetch(`${API_URL}/api/business-case-builder/analytics/hero-narrative-event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            event: 'clicked',
            kind: __heroNarrative.kind,
            metric_key: __heroNarrative.metricKey,
            headline: __renderedHeadline,
            delta: __heroNarrative.deltaText
              ? parseFloat(__heroNarrative.deltaText)
              : null,
            variant_id: __activeVariantId,
          }),
        }).catch(() => { /* best-effort */ });
      }
    } catch { /* swallow */ }
    setInlineCreateOpen(true);
  };

  const bcbHeroPanel = (
    <button
      onClick={handleHeroClick}
      className="group relative w-full text-left rounded-xl bg-white border border-slate-200 p-5 mt-4 hover:bg-slate-50 hover:border-slate-300 hover:-translate-y-0.5 transition-all flex flex-col text-slate-900 overflow-hidden shadow-sm"
      data-testid="bcb-new-roe-case-tile"
    >
      <div className="absolute -right-10 -top-10 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
        <Plus size={160} strokeWidth={1.5} />
      </div>
      <div className="flex flex-wrap items-center gap-2 relative">
        <div className="p-1.5 bg-slate-100 rounded-md">
          <Activity className="text-slate-700" size={14} />
        </div>
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">Org Health · Live</span>
        {healthSnapshot?.has_history && (
          <span className="text-[9px] text-slate-400 ml-1">vs {healthSnapshot.previous_quarter}</span>
        )}
        {__narrativeAccent && (
          <span
            className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${__narrativeAccent} ml-auto`}
            data-testid={`bcb-hero-narrative-kind-${__heroNarrative.kind}`}
          >
            {__heroNarrative.kind === 'decline' ? 'Action signal' : 'Momentum signal'}
          </span>
        )}
      </div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mt-3 relative">
        <div className="min-w-0">
          <h3 className="text-2xl md:text-3xl font-bold leading-tight text-slate-900 tracking-tight">
            Start a new business case
          </h3>
          <p
            className="text-sm md:text-base text-slate-600 mt-1.5"
            data-testid="bcb-hero-narrative"
            data-variant-id={__activeVariantId || ''}
          >
            {__renderedHeadline}
          </p>
        </div>
        <div className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 md:py-2 bg-[#44b6d2] text-white rounded-lg text-sm font-semibold shadow-sm group-hover:bg-[#3aa3bd] transition-colors w-full md:w-auto shrink-0">
          <Plus size={14} />
          Start modelling
          <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" />
        </div>
      </div>
      <div className="mt-4 relative">
        <HealthStatsStrip
          layout="inline"
          tone="light"
          prefetched={healthSnapshot}
          roeContextFallback={roeContext}
          testId="bcb-tile-health-strip"
        />
      </div>
    </button>
  );

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Solution Business Case"
        description="Create and export professional business cases from your Solution Finder analysis. Customize sections, enhance with AI, and export to PDF, Word, or PowerPoint."
        theme={__pageTheme}
        icon={Compass}
        actions={bcbHeroActions}
        stats={bcbHeroPanel}
        testId="bcb-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6 animate-fade-in">

        {/* Target Cohort Banner - if cohort was selected */}
        {solutionData?.cohort && (
          <div className="bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Users className="text-teal-600" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">Target Cohort: {solutionData.cohort.name}</h3>
                <p className="text-sm text-teal-600">
                  This business case targets a specific cohort for tracking and measurement
                </p>
              </div>
              <div className="text-right">
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                  Cohort-Specific Analysis
                </span>
              </div>
            </div>
          </div>
        )}
        
        {/* Business Cases grid — always-visible, selectable tiles.
            Includes a "+ New ROE-Validated Case" starter tile when
            ROE context is available. Clicking a saved tile loads
            the case into the editor. */}
        <div data-testid="bcb-cases-grid">
          <div className="flex items-center justify-between gap-4 mb-3 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Business Cases</h2>
              <p className="text-xs text-slate-500">Click a card to open it — edits can be saved as a variant to preserve the original.</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Search filter — narrows by title or KPI */}
              <input
                type="search"
                value={casesFilter}
                onChange={(e) => setCasesFilter(e.target.value)}
                placeholder="Filter cases…"
                className="text-sm rounded-lg px-3 py-1.5 border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-blue-300 w-44"
                data-testid="bcb-cases-filter-input"
              />
              {/* Inline-create entry point — always visible so users can
                  spin up a new case without hunting for the starter tile. */}
              <button
                onClick={() => setInlineCreateOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-lg bg-teal-600 text-white hover:bg-teal-700"
                data-testid="bcb-inline-create-btn"
              >
                <Plus size={14} />
                New case
              </button>
              {/* Recent / All segmented toggle */}
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="bcb-cases-view-toggle">
                {[
                  { id: 'recent', label: 'Recent' },
                  { id: 'all', label: 'All history' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    onClick={() => setCasesView(opt.id)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md ${casesView === opt.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                    data-testid={`bcb-cases-view-${opt.id}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* All-history breadcrumb — keeps users oriented after drilling deep */}
              {casesView === 'all' && savedCases.length > 0 && (
                <span className="text-[11px] text-slate-500" data-testid="bcb-all-history-breadcrumb">
                  Showing {savedCases.length} cases ·{' '}
                  <button
                    onClick={() => setCasesView('recent')}
                    className="text-blue-600 hover:underline font-medium"
                    data-testid="bcb-back-to-recent-btn"
                  >
                    Back to Recent
                  </button>
                </span>
              )}
              {/* Org filter — Super Admin only; defaults to viewAsOrg */}
              {isSuperAdmin && organizations.length > 1 && (
                <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}
                  className="text-sm rounded-lg px-3 py-1.5 border border-slate-200 bg-white text-slate-700 focus:ring-2 focus:ring-blue-300 min-w-[180px]"
                  data-testid="business-case-org-filter">
                  <option value="all">All Organizations</option>
                  {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
              )}
            </div>
          </div>
          {/* Cross-tenant insight (Super Admin · All Organizations) —
              rolls the per-source ROI heatmap up across every tenant
              the user can see, plus a top-5 orgs breakdown. Pure
              frontend aggregation over savedCases. */}
          {isSuperAdmin && selectedOrg === 'all' && organizations.length > 1 && savedCases.length > 0 && (() => {
            const orgNameById = Object.fromEntries(organizations.map((o) => [o.id, o.name]));
            const sourceOf = (c) => {
              const p = c.provenance;
              if (p?.constraint?.label) return 'constraint';
              if (p?.nav_condition?.label) return 'nav_condition';
              if (p?.pe_benchmarking?.condition) return 'pe_benchmarking';
              if (p?.industry_kpi?.kpi_name) return 'industry_kpi';
              return 'direct';
            };
            const sourceLabels = {
              constraint: 'From Mission Control',
              nav_condition: 'From Org Benchmarks',
              pe_benchmarking: 'From PE Benchmarking',
              industry_kpi: 'From Industry Benchmarks',
              direct: 'Direct',
            };
            const sourceTones = {
              constraint: 'bg-orange-100 text-orange-700 border-orange-200',
              nav_condition: 'bg-teal-100 text-teal-700 border-teal-200',
              pe_benchmarking: 'bg-violet-100 text-violet-700 border-violet-200',
              industry_kpi: 'bg-indigo-100 text-indigo-700 border-indigo-200',
              direct: 'bg-slate-100 text-slate-700 border-slate-200',
            };
            const rollupCases = (cases) => {
              if (!cases.length) return null;
              const roiM = []; let totalInv = 0;
              cases.forEach((c) => {
                const f = c.solution_data_summary?.financial_summary || {};
                if (f.roi_percent != null) roiM.push(1 + Number(f.roi_percent) / 100);
                if (f.total_investment != null) totalInv += Number(f.total_investment);
              });
              return {
                count: cases.length,
                avgRoiMultiple: roiM.length ? roiM.reduce((s, v) => s + v, 0) / roiM.length : null,
                totalInvestment: totalInv || null,
              };
            };
            // ── Rolling 90-day trend per source ──────────────────────
            // Bucket cases by 15-day windows over the last 180 days.
            // Last 6 buckets feed the sparkline; the average of those
            // vs the prior 6 buckets gives the QoQ delta arrow.
            const NOW = Date.now();
            const DAY = 24 * 60 * 60 * 1000;
            const BUCKET_DAYS = 15;
            const N_BUCKETS_CUR = 6; // 6 × 15 = 90 days current quarter
            const N_BUCKETS_PRIOR = 6; // 6 × 15 = 90 days prior quarter
            const trendForCases = (cases) => {
              if (!cases.length) return null;
              const cur = new Array(N_BUCKETS_CUR).fill(null).map(() => []);
              const prior = new Array(N_BUCKETS_PRIOR).fill(null).map(() => []);
              cases.forEach((c) => {
                const f = c.solution_data_summary?.financial_summary || {};
                if (f.roi_percent == null) return;
                const created = c.created_at ? new Date(c.created_at).getTime() : null;
                if (!created || Number.isNaN(created)) return;
                const ageDays = (NOW - created) / DAY;
                if (ageDays < 0) return;
                const roiM = 1 + Number(f.roi_percent) / 100;
                if (ageDays < N_BUCKETS_CUR * BUCKET_DAYS) {
                  const idx = Math.min(N_BUCKETS_CUR - 1, Math.floor(ageDays / BUCKET_DAYS));
                  // idx=0 is most recent — flip to render oldest-first
                  cur[N_BUCKETS_CUR - 1 - idx].push(roiM);
                } else if (ageDays < (N_BUCKETS_CUR + N_BUCKETS_PRIOR) * BUCKET_DAYS) {
                  const idx = Math.floor((ageDays - N_BUCKETS_CUR * BUCKET_DAYS) / BUCKET_DAYS);
                  prior[N_BUCKETS_PRIOR - 1 - idx].push(roiM);
                }
              });
              const avg = (a) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : null);
              const curSeries = cur.map(avg);
              const priorSeries = prior.map(avg);
              const flatCur = curSeries.filter((v) => v != null);
              const flatPrior = priorSeries.filter((v) => v != null);
              if (!flatCur.length) return null;
              const curAvg = avg(flatCur);
              const priorAvg = flatPrior.length ? avg(flatPrior) : null;
              return {
                series: curSeries, // length 6, oldest→newest, may contain nulls
                curAvg,
                priorAvg,
                qoqDelta: priorAvg != null ? curAvg - priorAvg : null,
              };
            };
            // Per-source rollup across ALL tenants
            const bySource = { constraint: [], nav_condition: [], pe_benchmarking: [], industry_kpi: [], direct: [] };
            savedCases.forEach((c) => { bySource[sourceOf(c)].push(c); });
            const sourceRollups = Object.entries(bySource)
              .map(([src, cs]) => ({
                src,
                label: sourceLabels[src],
                tone: sourceTones[src],
                trend: trendForCases(cs),
                ...(rollupCases(cs) || { count: 0, avgRoiMultiple: null, totalInvestment: null }),
              }))
              .filter((r) => r.count > 0);
            // Highest-leverage source = highest avg ROI multiple (need ≥2 sources to claim a winner)
            // Highest-leverage entry point — the source with the best avg
            // ROI multiple across tenants. Always names a leader when any
            // ROI-bearing source exists, with a small-sample caveat so an
            // early leader isn't over-claimed.
            const roiRanked = [...sourceRollups]
              .filter((r) => r.avgRoiMultiple != null)
              .sort((a, b) => b.avgRoiMultiple - a.avgRoiMultiple);
            const leader = roiRanked[0] || null;
            const leaderRunnerUp = roiRanked[1] || null;
            const leaderDelta = leader && leaderRunnerUp
              ? leader.avgRoiMultiple - leaderRunnerUp.avgRoiMultiple
              : null;
            const leaderLowConfidence = leader ? (leader.count < 2 || roiRanked.length < 2) : true;
            // Per-org top-5 by case count
            const byOrg = {};
            savedCases.forEach((c) => {
              const oid = c.organization_id || 'unknown';
              (byOrg[oid] = byOrg[oid] || []).push(c);
            });
            const tenantsVisible = Object.keys(byOrg).length;
            const topOrgs = Object.entries(byOrg)
              .map(([oid, cases]) => ({
                oid,
                name: orgNameById[oid] || oid,
                ...(rollupCases(cases) || { count: 0, avgRoiMultiple: null, totalInvestment: null }),
              }))
              .sort((a, b) => b.count - a.count)
              .slice(0, 5);
            const fmtX = (v) => (v == null ? '—' : `${v.toFixed(1)}x`);
            const fmtMoney0 = (v) =>
              v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
            return (
              <div
                className="rounded-xl border border-blue-200 bg-gradient-to-br from-blue-50/70 to-indigo-50/40 p-4 mb-4"
                data-testid="bcb-cross-tenant-panel"
              >
                <div className="flex items-baseline gap-2 mb-3 flex-wrap">
                  <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-blue-700">
                    Cross-tenant insight
                  </span>
                  <span className="text-[11px] text-slate-600">
                    {tenantsVisible} tenants visible · {savedCases.length} cases total
                  </span>
                </div>
                {leader && (
                  <p className="text-sm text-slate-800 mb-3 leading-snug" data-testid="bcb-cross-tenant-headline">
                    <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-blue-700 mr-1.5">Highest-leverage entry point</span>
                    cases{' '}
                    <button
                      type="button"
                      onClick={() => setProvenanceFilter(leader.src === 'direct' ? 'direct' : leader.src)}
                      className="font-bold text-blue-700 hover:text-blue-900 underline decoration-dotted underline-offset-2 transition-colors"
                      data-testid="bcb-cross-tenant-headline-leader"
                      title={`Filter the library to cases ${leader.label}`}
                    >
                      {leader.label}
                    </button>{' '}
                    average{' '}
                    <strong className="tabular-nums">{fmtX(leader.avgRoiMultiple)}</strong> ROI across {tenantsVisible} tenant{tenantsVisible === 1 ? '' : 's'}
                    {leaderDelta != null && leaderDelta >= 0.1 && leaderRunnerUp && (
                      <> — <strong className="tabular-nums">{leaderDelta.toFixed(1)}x</strong> ahead of {leaderRunnerUp.label}</>
                    )}.
                    {leaderLowConfidence && (
                      <span className="text-slate-500" data-testid="bcb-cross-tenant-headline-caveat">
                        {' '}Early signal — based on {leader.count} case{leader.count === 1 ? '' : 's'}; treat as directional until more cases land.
                      </span>
                    )}
                  </p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="bcb-cross-tenant-source-grid">
                  {sourceRollups.map((r) => {
                    const isChampion = leader?.src === r.src;
                    // ── Sparkline: scale series into a 60×16 viewbox ──
                    const sparkSeries = r.trend?.series || [];
                    const validPoints = sparkSeries
                      .map((v, i) => ({ x: i, y: v }))
                      .filter((p) => p.y != null);
                    let sparkPath = '';
                    if (validPoints.length >= 2) {
                      const ys = validPoints.map((p) => p.y);
                      const minY = Math.min(...ys);
                      const maxY = Math.max(...ys);
                      const range = maxY - minY || 1;
                      const W = 60;
                      const H = 16;
                      sparkPath = validPoints
                        .map((p, idx) => {
                          const x = (p.x / (N_BUCKETS_CUR - 1)) * W;
                          const y = H - ((p.y - minY) / range) * H;
                          return `${idx === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
                        })
                        .join(' ');
                    }
                    // ── QoQ arrow ──
                    const qoq = r.trend?.qoqDelta;
                    let arrowLabel = null;
                    if (qoq != null && Math.abs(qoq) >= 0.1) {
                      const dir = qoq > 0 ? '↑' : '↓';
                      arrowLabel = (
                        <span
                          className={`text-[10px] font-semibold tabular-nums ${
                            qoq > 0 ? 'text-emerald-700' : 'text-rose-700'
                          }`}
                          data-testid={`bcb-cross-tenant-source-${r.src}-qoq`}
                          data-qoq-direction={qoq > 0 ? 'up' : 'down'}
                        >
                          {dir} {qoq > 0 ? '+' : ''}
                          {qoq.toFixed(1)}x QoQ
                        </span>
                      );
                    } else if (qoq != null) {
                      arrowLabel = (
                        <span
                          className="text-[10px] font-semibold text-slate-500 tabular-nums"
                          data-testid={`bcb-cross-tenant-source-${r.src}-qoq`}
                          data-qoq-direction="flat"
                        >
                          → flat QoQ
                        </span>
                      );
                    }
                    return (
                      <button
                        type="button"
                        key={r.src}
                        onClick={() => setCrossTenantDrillSource((cur) => (cur === r.src ? null : r.src))}
                        className={`text-left rounded-lg border px-3 py-2 ${r.tone} ${isChampion ? 'ring-2 ring-offset-1 ring-blue-400' : ''} ${crossTenantDrillSource === r.src ? 'ring-2 ring-offset-1 ring-slate-700' : ''} hover:shadow-sm transition-shadow cursor-pointer`}
                        data-testid={`bcb-cross-tenant-source-${r.src}`}
                        data-active={crossTenantDrillSource === r.src}
                      >
                        <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">
                          {r.label}
                        </div>
                        <div className="flex items-end justify-between gap-2 mt-0.5">
                          <div className="text-lg font-bold text-slate-900 tabular-nums">
                            {fmtX(r.avgRoiMultiple)}
                          </div>
                          {sparkPath && (
                            <svg
                              viewBox="0 0 60 16"
                              className="w-[60px] h-[16px] flex-shrink-0 opacity-80"
                              data-testid={`bcb-cross-tenant-source-${r.src}-sparkline`}
                              aria-label={`90-day ROI trend for ${r.label}`}
                            >
                              <path
                                d={sparkPath}
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>
                        <div className="flex items-center justify-between gap-2 mt-0.5">
                          <div className="text-[10px] text-slate-600">
                            {r.count} cases · {fmtMoney0(r.totalInvestment)}
                          </div>
                          {arrowLabel}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {/* Drill-down drawer — appears when a source tile is
                    clicked. Shows the 5 most recent cases from that
                    source + a "biggest swing" callout identifying
                    which case is dragging the average. */}
                {crossTenantDrillSource && (() => {
                  const src = crossTenantDrillSource;
                  const bucket = (bySource[src] || []).slice().sort((a, b) => {
                    const ta = new Date(a.created_at || 0).getTime();
                    const tb = new Date(b.created_at || 0).getTime();
                    return tb - ta;
                  });
                  if (!bucket.length) return null;
                  const sourceRoll = sourceRollups.find((r) => r.src === src);
                  const avg = sourceRoll?.avgRoiMultiple;
                  // Identify biggest positive and negative deviations vs the source avg
                  const withRoi = bucket
                    .map((c) => {
                      const f = c.solution_data_summary?.financial_summary || {};
                      const roi = f.roi_percent != null ? 1 + Number(f.roi_percent) / 100 : null;
                      return { c, roi };
                    })
                    .filter((r) => r.roi != null);
                  let dragsUp = null;
                  let dragsDown = null;
                  if (avg != null && withRoi.length >= 2) {
                    const sortedByDev = [...withRoi].sort((a, b) => b.roi - a.roi);
                    dragsUp = sortedByDev[0]; // highest
                    dragsDown = sortedByDev[sortedByDev.length - 1]; // lowest
                    if (dragsUp.roi <= avg) dragsUp = null;
                    if (dragsDown.roi >= avg) dragsDown = null;
                  }
                  const recent = bucket.slice(0, 5);
                  const fmtAge = (iso) => {
                    if (!iso) return '—';
                    const days = Math.max(0, Math.round((NOW - new Date(iso).getTime()) / DAY));
                    if (days === 0) return 'today';
                    if (days < 30) return `${days}d ago`;
                    if (days < 365) return `${Math.round(days / 30)}mo ago`;
                    return `${Math.round(days / 365)}y ago`;
                  };
                  return (
                    <div
                      className="rounded-lg border border-slate-300 bg-white/90 backdrop-blur-sm p-4 mb-4"
                      data-testid={`bcb-cross-tenant-drill-${src}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-slate-600">
                            Drill-down · {sourceLabels[src]}
                          </div>
                          <div className="text-sm text-slate-700">
                            {bucket.length} cases · avg {fmtX(avg)}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCrossTenantDrillSource(null)}
                          className="text-slate-400 hover:text-slate-700 text-xs underline"
                          data-testid="bcb-cross-tenant-drill-close"
                        >
                          Close
                        </button>
                      </div>
                      {(dragsUp || dragsDown) && (
                        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 mb-3 text-xs text-slate-700 leading-snug" data-testid="bcb-cross-tenant-drill-swing">
                          <span className="font-bold text-[10px] uppercase tracking-wider text-slate-600 mr-2">Biggest swings</span>
                          {dragsUp && (
                            <span className="block mt-1">
                              <span className="text-emerald-700 font-semibold">↑ {dragsUp.c.title}</span>{' '}
                              at <span className="tabular-nums">{fmtX(dragsUp.roi)}</span> is dragging the average up.
                            </span>
                          )}
                          {dragsDown && (
                            <span className="block mt-1">
                              <span className="text-rose-700 font-semibold">↓ {dragsDown.c.title}</span>{' '}
                              at <span className="tabular-nums">{fmtX(dragsDown.roi)}</span> is the heaviest weight pulling the trend down.
                            </span>
                          )}
                        </div>
                      )}
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
                        5 most recent cases
                      </div>
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-left text-slate-500 border-b border-slate-200">
                            <th className="py-1.5 pr-3 font-medium">Title</th>
                            <th className="py-1.5 pr-3 font-medium">KPI</th>
                            <th className="py-1.5 pr-3 font-medium tabular-nums">ROI</th>
                            <th className="py-1.5 pr-3 font-medium tabular-nums">vs avg</th>
                            <th className="py-1.5 font-medium tabular-nums">Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {recent.map((c) => {
                            const f = c.solution_data_summary?.financial_summary || {};
                            const roi = f.roi_percent != null ? 1 + Number(f.roi_percent) / 100 : null;
                            const dev = roi != null && avg != null ? roi - avg : null;
                            return (
                              <tr key={c.id} className="border-b border-slate-100 last:border-b-0" data-testid={`bcb-cross-tenant-drill-row-${c.id}`}>
                                <td className="py-1.5 pr-3 font-medium text-slate-800 truncate max-w-[280px]">{c.title}</td>
                                <td className="py-1.5 pr-3 text-slate-600 truncate max-w-[140px]">{c.solution_data_summary?.kpi_name || '—'}</td>
                                <td className="py-1.5 pr-3 text-slate-700 tabular-nums">{fmtX(roi)}</td>
                                <td className={`py-1.5 pr-3 tabular-nums ${
                                  dev == null ? 'text-slate-400' : dev > 0.05 ? 'text-emerald-700' : dev < -0.05 ? 'text-rose-700' : 'text-slate-500'
                                }`}>
                                  {dev == null ? '—' : `${dev > 0 ? '+' : ''}${dev.toFixed(1)}x`}
                                </td>
                                <td className="py-1.5 text-slate-600 tabular-nums">{fmtAge(c.created_at)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })()}
                {topOrgs.length > 1 && (
                  <div data-testid="bcb-cross-tenant-top-orgs">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1.5">
                      Top {topOrgs.length} tenants by case count
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-slate-500 border-b border-blue-200">
                          <th className="py-1.5 pr-3 font-medium">Organization</th>
                          <th className="py-1.5 pr-3 font-medium tabular-nums">Cases</th>
                          <th className="py-1.5 pr-3 font-medium tabular-nums">Avg ROI</th>
                          <th className="py-1.5 font-medium tabular-nums">Total invested</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topOrgs.map((o) => (
                          <tr key={o.oid} className="border-b border-blue-100 last:border-b-0" data-testid={`bcb-cross-tenant-org-${o.oid}`}>
                            <td className="py-1.5 pr-3 font-medium text-slate-800 truncate max-w-[260px]">{o.name}</td>
                            <td className="py-1.5 pr-3 text-slate-700 tabular-nums">{o.count}</td>
                            <td className="py-1.5 pr-3 text-slate-700 tabular-nums">{fmtX(o.avgRoiMultiple)}</td>
                            <td className="py-1.5 text-slate-700 tabular-nums">{fmtMoney0(o.totalInvestment)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
          {/* Provenance source filter — chips show counts per
              narrative-bridge source so the library becomes a
              queryable archive of decision context. Hidden until at
              least one provenance-stamped case exists (otherwise
              every chip would read "0"). */}
          {(() => {
            // ── Build a per-source bucket once, then compute counts +
            // financial rollups against the same bucket. Keeps the
            // chip tooltips and the stat strip below in lockstep.
            const sourceOf = (c) => {
              const p = c.provenance;
              if (p?.constraint?.label) return 'constraint';
              if (p?.nav_condition?.label) return 'nav_condition';
              if (p?.pe_benchmarking?.condition) return 'pe_benchmarking';
              if (p?.kpi_alignment?.kpi_name) return 'kpi_alignment';
              if (p?.industry_kpi?.kpi_name) return 'industry_kpi';
              return 'direct';
            };
            const buckets = { all: savedCases, constraint: [], nav_condition: [], pe_benchmarking: [], kpi_alignment: [], industry_kpi: [], direct: [] };
            savedCases.forEach((c) => {
              const s = sourceOf(c);
              if (buckets[s]) buckets[s].push(c);
            });
            const rollup = (cases) => {
              if (!cases.length) return null;
              const roiMultiples = [];
              let totalInvestment = 0;
              const kpiPcts = [];
              const paybacks = [];
              cases.forEach((c) => {
                const f = c.solution_data_summary?.financial_summary || {};
                if (f.roi_percent != null) roiMultiples.push(1 + Number(f.roi_percent) / 100);
                if (f.total_investment != null) totalInvestment += Number(f.total_investment);
                if (f.kpi_uplift_percent != null) kpiPcts.push(Number(f.kpi_uplift_percent));
                if (f.payback_months != null) paybacks.push(Number(f.payback_months));
              });
              const avg = (arr) => arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : null;
              return {
                count: cases.length,
                avgRoiMultiple: avg(roiMultiples),
                totalInvestment: totalInvestment || null,
                avgKpiUplift: avg(kpiPcts),
                avgPayback: avg(paybacks),
              };
            };
            const stats = {
              all: rollup(buckets.all),
              constraint: rollup(buckets.constraint),
              nav_condition: rollup(buckets.nav_condition),
              pe_benchmarking: rollup(buckets.pe_benchmarking),
              kpi_alignment: rollup(buckets.kpi_alignment),
              industry_kpi: rollup(buckets.industry_kpi),
              direct: rollup(buckets.direct),
            };
            const fmtMoney0 = (v) =>
              v == null ? '—' : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
            const fmtX = (v) => (v == null ? '—' : `${v.toFixed(1)}x`);
            const fmtPct1 = (v) => (v == null ? '—' : `${v.toFixed(1)}%`);
            const fmtMo = (v) => (v == null ? '—' : `${v.toFixed(1)}mo`);
            const tooltipFor = (chipId) => {
              const s = chipId ? stats[chipId] : stats.all;
              if (!s || !s.count) return chipId === null ? 'All cases' : 'No cases yet';
              return [
                `${s.count} cases`,
                `avg ROI ${fmtX(s.avgRoiMultiple)}`,
                s.totalInvestment ? `total ${fmtMoney0(s.totalInvestment)}` : null,
                s.avgKpiUplift != null ? `avg KPI uplift ${fmtPct1(s.avgKpiUplift)}` : null,
                s.avgPayback != null ? `payback ~${fmtMo(s.avgPayback)}` : null,
              ].filter(Boolean).join(' · ');
            };

            const hasAnyProvenance =
              (stats.constraint?.count || 0) +
                (stats.nav_condition?.count || 0) +
                (stats.pe_benchmarking?.count || 0) +
                (stats.kpi_alignment?.count || 0) +
                (stats.industry_kpi?.count || 0) >
              0;
            if (!hasAnyProvenance) return null;
            const chips = [
              { id: null, label: 'All', tone: 'slate' },
              { id: 'constraint', label: 'From Mission Control', tone: 'orange' },
              { id: 'nav_condition', label: 'From Org Benchmarks', tone: 'teal' },
              { id: 'pe_benchmarking', label: 'From PE Benchmarking', tone: 'violet' },
              { id: 'kpi_alignment', label: 'From KPI Alignment', tone: 'rose' },
              { id: 'industry_kpi', label: 'From Industry Benchmarks', tone: 'indigo' },
              { id: 'direct', label: 'Direct', tone: 'slate' },
            ].map((c) => ({ ...c, count: (c.id ? stats[c.id]?.count : stats.all?.count) || 0 }));
            const tones = {
              slate: {
                active: 'bg-slate-900 text-white border-slate-900',
                inactive: 'bg-white text-slate-700 border-slate-200 hover:border-slate-400',
                stripBorder: 'border-slate-200',
                stripBg: 'bg-slate-50',
                stripEyebrow: 'text-slate-700',
              },
              orange: {
                active: 'bg-orange-600 text-white border-orange-600',
                inactive: 'bg-white text-orange-700 border-orange-200 hover:border-orange-400',
                stripBorder: 'border-orange-200',
                stripBg: 'bg-orange-50',
                stripEyebrow: 'text-orange-700',
              },
              teal: {
                active: 'bg-teal-600 text-white border-teal-600',
                inactive: 'bg-white text-teal-700 border-teal-200 hover:border-teal-400',
                stripBorder: 'border-teal-200',
                stripBg: 'bg-teal-50',
                stripEyebrow: 'text-teal-700',
              },
              indigo: {
                active: 'bg-indigo-600 text-white border-indigo-600',
                inactive: 'bg-white text-indigo-700 border-indigo-200 hover:border-indigo-400',
                stripBorder: 'border-indigo-200',
                stripBg: 'bg-indigo-50',
                stripEyebrow: 'text-indigo-700',
              },
              violet: {
                active: 'bg-violet-600 text-white border-violet-600',
                inactive: 'bg-white text-violet-700 border-violet-200 hover:border-violet-400',
                stripBorder: 'border-violet-200',
                stripBg: 'bg-violet-50',
                stripEyebrow: 'text-violet-700',
              },
              rose: {
                active: 'bg-rose-600 text-white border-rose-600',
                inactive: 'bg-white text-rose-700 border-rose-200 hover:border-rose-400',
                stripBorder: 'border-rose-200',
                stripBg: 'bg-rose-50',
                stripEyebrow: 'text-rose-700',
              },
            };
            const activeChip = chips.find((c) => c.id === provenanceFilter);
            const activeStats = provenanceFilter ? stats[provenanceFilter] : stats.all;
            const activePalette = tones[activeChip?.tone || 'slate'];
            return (
              <>
                <div
                  className="flex flex-wrap items-center gap-1.5 mb-3"
                  data-testid="bcb-provenance-filter-chips"
                >
                  <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mr-1">
                    Source
                  </span>
                  {chips.map((chip) => {
                    const active = provenanceFilter === chip.id;
                    const palette = tones[chip.tone];
                    const disabled = chip.count === 0 && chip.id !== null;
                    return (
                      <button
                        key={chip.id ?? 'all'}
                        type="button"
                        onClick={() => setProvenanceFilter(chip.id)}
                        disabled={disabled}
                        title={tooltipFor(chip.id)}
                        className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          active ? palette.active : palette.inactive
                        }`}
                        data-testid={`bcb-provenance-chip-${chip.id ?? 'all'}`}
                        data-active={active}
                      >
                        <span>{chip.label}</span>
                        <span
                          className={`text-[10px] tabular-nums rounded px-1.5 ${
                            active ? 'bg-white/20' : 'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {chip.count}
                        </span>
                      </button>
                    );
                  })}
                  {provenanceFilter !== null && (
                    <button
                      type="button"
                      onClick={() => setProvenanceFilter(null)}
                      className="text-[11px] text-slate-500 hover:text-slate-800 underline ml-1"
                      data-testid="bcb-provenance-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {/* Per-source ROI heatmap strip — shows live financial
                    rollups for whichever filter is active (or for
                    "All" when no filter). Pulls straight from
                    case.solution_data_summary.financial_summary. */}
                {activeStats && activeStats.count > 0 && (
                  <div
                    className={`rounded-lg border ${activePalette.stripBorder} ${activePalette.stripBg} px-3 py-2 mb-4 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-xs`}
                    data-testid="bcb-provenance-stats-strip"
                    data-source={provenanceFilter ?? 'all'}
                  >
                    <span className={`text-[10px] uppercase tracking-wider font-bold ${activePalette.stripEyebrow}`}>
                      {activeChip ? activeChip.label : 'All cases'}
                    </span>
                    <span className="text-slate-700">
                      <strong className="text-slate-900 tabular-nums">{activeStats.count}</strong> cases
                    </span>
                    <span className="text-slate-700">
                      avg ROI <strong className="text-slate-900 tabular-nums">{fmtX(activeStats.avgRoiMultiple)}</strong>
                    </span>
                    {activeStats.totalInvestment != null && (
                      <span className="text-slate-700">
                        total investment <strong className="text-slate-900 tabular-nums">{fmtMoney0(activeStats.totalInvestment)}</strong>
                      </span>
                    )}
                    {activeStats.avgKpiUplift != null && (
                      <span className="text-slate-700">
                        avg KPI uplift <strong className="text-slate-900 tabular-nums">{fmtPct1(activeStats.avgKpiUplift)}</strong>
                      </span>
                    )}
                    {activeStats.avgPayback != null && (
                      <span className="text-slate-700">
                        payback ~<strong className="text-slate-900 tabular-nums">{fmtMo(activeStats.avgPayback)}</strong>
                      </span>
                    )}
                  </div>
                )}
              </>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {/* Saved case tiles */}
            {(() => {
              // Apply Recent/All view + free-text + provenance-source
              // filter to the cases list. Empty term/null filter both
              // mean "match everything".
              const term = casesFilter.trim().toLowerCase();
              const filtered = savedCases.filter(c => {
                if (term) {
                  const haystack = `${c.title || ''} ${c.description || ''} ${c.solution_data_summary?.kpi_name || ''}`.toLowerCase();
                  if (!haystack.includes(term)) return false;
                }
                if (provenanceFilter) {
                  const p = c.provenance;
                  if (provenanceFilter === 'constraint') return Boolean(p?.constraint?.label);
                  if (provenanceFilter === 'nav_condition') return Boolean(p?.nav_condition?.label);
                  if (provenanceFilter === 'pe_benchmarking') return Boolean(p?.pe_benchmarking?.condition);
                  if (provenanceFilter === 'kpi_alignment') return Boolean(p?.kpi_alignment?.kpi_name);
                  if (provenanceFilter === 'industry_kpi') return Boolean(p?.industry_kpi?.kpi_name);
                  if (provenanceFilter === 'direct') {
                    return !p ||
                      (!p.constraint?.label &&
                        !p.nav_condition?.label &&
                        !p.pe_benchmarking?.condition &&
                        !p.kpi_alignment?.kpi_name &&
                        !p.industry_kpi?.kpi_name);
                  }
                }
                return true;
              });
              const RECENT_LIMIT = 6;
              const visible = casesView === 'recent' ? filtered.slice(0, RECENT_LIMIT) : filtered;
              const hiddenCount = casesView === 'recent' ? Math.max(0, filtered.length - RECENT_LIMIT) : 0;
              // Best-ROI per family computed over ALL cases (not the
              // filtered subset) so the "Top" badge stays consistent.
              const bestRoiByFamily = {};
              savedCases.forEach(c => {
                const familyId = c.parent_case_id || c.id;
                const roi = c.solution_data_summary?.financial_summary?.roi_percent;
                if (roi == null) return;
                const cur = bestRoiByFamily[familyId];
                if (!cur || roi > cur.value) {
                  bestRoiByFamily[familyId] = { id: c.id, value: roi };
                }
              });
              const tiles = visible.map(c => {
                const isActive = savedCaseId === c.id;
                const roeSnap = c.roe_snapshot;
                const fin = c.solution_data_summary?.financial_summary || {};
                const isVariant = Boolean(c.parent_case_id);
                const familyId = c.parent_case_id || c.id;
                const isBestRoi = bestRoiByFamily[familyId]?.id === c.id
                  && Object.keys(bestRoiByFamily).length > 0
                  && (variantCountByParent[familyId] > 0 || isVariant); // only flag when family has ≥2 cases
                const roiPct = fin.roi_percent;
                const kpiPct = fin.kpi_uplift_percent;
                const fmt = (v, digits = 1) => v == null ? null : `${Number(v).toFixed(digits)}%`;
                // ROI is displayed as a return multiple ("2.8x") rather
                // than a percent. Formula: net-benefit % → gross multiple
                // = 1 + (roi_percent / 100). 179% → 2.8x.
                const fmtRoiX = (v) => v == null ? null : `${(1 + Number(v) / 100).toFixed(1)}x`;
                return (
                  <button
                    key={c.id}
                    onClick={() => handleLoadCase(c.id)}
                    className={`group relative rounded-xl border p-4 text-left hover:shadow-md transition-all flex flex-col ${
                      isActive
                        ? 'border-blue-500 bg-blue-50/50 ring-2 ring-blue-300'
                        : isBestRoi
                          ? 'border-emerald-300 bg-gradient-to-br from-emerald-50/40 to-white hover:border-emerald-400'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                    }`}
                    data-testid={`bcb-case-tile-${c.id}`}
                  >
                    {/* Title row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <FileText className="text-slate-400 flex-shrink-0" size={16} />
                        <span className="text-sm font-semibold text-slate-900 truncate">{c.title}</span>
                        {c.is_preferred && (
                          <Star size={12} className="text-amber-500 flex-shrink-0" fill="currentColor" />
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {isActive && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">Open</span>
                        )}
                        {c.status && c.status !== 'draft' && (
                          <ApprovalStatusChip status={c.status} size="sm" testId={`bcb-case-status-${c.id}`} />
                        )}
                        {isVariant && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-800">Variant</span>
                        )}
                        {isBestRoi && !isActive && (
                          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-800" title="Best ROI in this family">
                            <TrendingUp size={9} /> Top
                          </span>
                        )}
                      </div>
                    </div>
                    {c.solution_data_summary?.kpi_name && (
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">{c.solution_data_summary.kpi_name}</div>
                    )}
                    {c.provenance && (
                      <div className="mt-1.5">
                        <ProvenanceBadges
                          provenance={c.provenance}
                          size="xs"
                          testIdPrefix={`bcb-case-${c.id}-provenance`}
                        />
                      </div>
                    )}

                    {/* Headline metrics — ROE / PE Valid / KPI Uplift / ROI */}
                    <div className="grid grid-cols-4 gap-1.5 mt-3 mb-1">
                      <div className="rounded-md bg-teal-50 border border-teal-100 p-1.5 text-center">
                        <div className="text-[9px] font-bold uppercase tracking-wide text-teal-700">ROE</div>
                        <div className="text-sm font-bold text-teal-700 leading-tight">{roeSnap?.roe_pct != null ? fmt(roeSnap.roe_pct) : '—'}</div>
                      </div>
                      <div className="rounded-md bg-purple-50 border border-purple-100 p-1.5 text-center">
                        <div className="text-[9px] font-bold uppercase tracking-wide text-purple-700">PE Valid</div>
                        <div className="text-sm font-bold text-purple-700 leading-tight">{roeSnap?.pe_validated_pct != null ? fmt(roeSnap.pe_validated_pct) : '—'}</div>
                      </div>
                      <div className="rounded-md bg-amber-50 border border-amber-100 p-1.5 text-center">
                        <div className="text-[9px] font-bold uppercase tracking-wide text-amber-700">KPI %</div>
                        <div className="text-sm font-bold text-amber-700 leading-tight">{kpiPct != null ? fmt(kpiPct, 2) : '—'}</div>
                      </div>
                      <div className={`rounded-md border p-1.5 text-center ${isBestRoi ? 'bg-emerald-100 border-emerald-300' : 'bg-emerald-50 border-emerald-100'}`}>
                        <div className="text-[9px] font-bold uppercase tracking-wide text-emerald-700">ROI</div>
                        <div className={`text-sm font-bold leading-tight ${isBestRoi ? 'text-emerald-800' : 'text-emerald-700'}`}>{roiPct != null ? fmtRoiX(roiPct) : '—'}</div>
                      </div>
                    </div>

                    {/* Footer row: updated date + action buttons */}
                    <div className="mt-auto pt-3 flex items-center justify-between">
                      <span className="text-[11px] text-slate-400">
                        {c.updated_at ? `Updated ${new Date(c.updated_at).toLocaleDateString()}` : ''}
                      </span>
                      <div className="flex items-center gap-1">
                        {variantCountByParent[c.id] > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenCompare(c.id); }}
                            className="flex items-center gap-1 px-1.5 py-1 text-[11px] font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 rounded"
                            title={`Compare with ${variantCountByParent[c.id]} variant${variantCountByParent[c.id] > 1 ? 's' : ''}`}
                            data-testid={`bcb-case-compare-${c.id}`}
                          >
                            <GitCompare size={12} />
                            Compare ({variantCountByParent[c.id]})
                          </button>
                        )}
                        <button
                          onClick={(e) => handleDeleteCase(c.id, e)}
                          className="p-1 text-slate-300 hover:text-rose-600 hover:bg-rose-50 rounded"
                          title="Delete"
                          data-testid={`bcb-case-delete-${c.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                    {isActive && (
                      <span className="absolute inset-0 rounded-xl pointer-events-none" />
                    )}
                  </button>
                );
              });
              if (hiddenCount > 0) {
                tiles.push(
                  <button
                    key="__show-all"
                    onClick={() => setCasesView('all')}
                    className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400 p-4 flex flex-col items-center justify-center text-center min-h-[200px] transition-colors"
                    data-testid="bcb-show-all-tile"
                  >
                    <ChevronDown className="text-slate-500 mb-2" size={28} />
                    <span className="text-sm font-semibold text-slate-700">Show all {filtered.length} cases</span>
                    <span className="text-xs text-slate-500 mt-0.5">{hiddenCount} more hidden</span>
                  </button>
                );
              }
              return tiles;
            })()}
            
            {/* Empty state */}
            {savedCases.length === 0 && !roeContext?.has_data && !casesFilter && (
              <div className="col-span-full rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center">
                <FileText className="mx-auto text-slate-300 mb-3" size={32} />
                <p className="text-sm font-medium text-slate-900">No business cases yet</p>
                <p className="text-xs text-slate-500 mt-1">Start from the Solution Finder to generate your first case.</p>
                <button
                  onClick={() => navigate('/solution-finder')}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700"
                >
                  Go to Solution Finder
                </button>
              </div>
            )}
            {/* Filter empty state — separate so Health-Stats CTA stays visible */}
            {savedCases.length > 0 && casesFilter && savedCases.filter(c => {
              const term = casesFilter.trim().toLowerCase();
              const haystack = `${c.title || ''} ${c.description || ''} ${c.solution_data_summary?.kpi_name || ''}`.toLowerCase();
              return haystack.includes(term);
            }).length === 0 && (
              <div className="col-span-full rounded-xl border border-dashed border-slate-200 bg-white p-6 text-center">
                <p className="text-sm font-medium text-slate-700">No cases match "{casesFilter}"</p>
                <button onClick={() => setCasesFilter('')} className="mt-2 text-xs font-semibold text-blue-600 hover:underline">Clear filter</button>
              </div>
            )}
          </div>
        </div>
        
        {/* Alerts */}
        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="text-rose-600" size={20} />
            <span className="text-rose-700">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto">
              <X size={18} className="text-rose-600" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center gap-3">
            <CheckCircle2 className="text-emerald-600" size={20} />
            <span className="text-emerald-700">{success}</span>
          </div>
        )}
        
        {/* No Content State — when no sections generated */}
        {sections.length === 0 && (
          <div className="bg-white rounded-xl border p-12 text-center">
            <FileText className="mx-auto text-gray-400 mb-4" size={48} />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Business Case Data</h2>
            <p className="text-gray-600 mb-6">
              Start from the Solution Finder to generate a business case, or open a saved case above.
            </p>
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => navigate('/solution-finder')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Go to Solution Finder
              </button>
            </div>
          </div>
        )}
        
        {/* Editor Content */}
        {sections.length > 0 && (
          <>
            {/* Competitive Urgency / Top-Quartile Stretch banner — only
                when the org has a meaningful industry-benchmark story
                on this KPI. Tone="urgency" (below median) → amber/rose;
                tone="stretch" (above median, < top-decile) → teal. Mirrors
                the same context stamped into the AI Executive Summary +
                exports. */}
            {benchmarkContext && (() => {
              const tone = benchmarkContext.tone || 'urgency';
              const isStretch = tone === 'stretch';
              const isBottomQuartile = benchmarkContext.is_bottom_quartile;
              const palette = isStretch
                ? { border: 'border-teal-300', bg: 'bg-teal-50', iconBg: 'bg-teal-100 text-teal-700', label: 'text-teal-700', chip: 'bg-teal-200 text-teal-900', gapText: 'text-teal-700' }
                : isBottomQuartile
                  ? { border: 'border-rose-300', bg: 'bg-rose-50', iconBg: 'bg-rose-100 text-rose-700', label: 'text-rose-700', chip: 'bg-rose-200 text-rose-800', gapText: 'text-rose-700' }
                  : { border: 'border-amber-300', bg: 'bg-amber-50', iconBg: 'bg-amber-100 text-amber-700', label: 'text-amber-700', chip: 'bg-amber-200 text-amber-800', gapText: 'text-rose-700' };
              const labelText = isStretch ? 'Top-quartile stretch' : 'Competitive urgency';
              const Icon = isStretch ? TrendingUp : AlertTriangle;
              return (
              <div
                className={`mb-6 rounded-xl border p-5 flex items-start gap-4 ${palette.border} ${palette.bg}`}
                data-testid="bcb-competitive-urgency-banner"
                data-tone={tone}
              >
                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${palette.iconBg}`}>
                  <Icon size={20} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className={`text-[10px] font-bold uppercase tracking-wider ${palette.label}`}>
                      {labelText}
                    </span>
                    <span
                      className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${palette.chip}`}
                      data-testid="bcb-urgency-percentile-chip"
                    >
                      {benchmarkContext.ordinal} percentile · {benchmarkContext.industry_name}
                    </span>
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {benchmarkContext.headline}
                  </div>
                  <p className="mt-1.5 text-xs text-slate-700 leading-relaxed">
                    {benchmarkContext.narrative}
                  </p>
                  {/* Drift bar — surfaces ONLY when the prior snapshot
                      and current context differ ≥5 points or flip tier.
                      Green for improvement, amber for moderate slip,
                      rose for steep slip / worse-tier flip. */}
                  {benchmarkDrift && (
                    <div
                      className={`mt-2.5 flex items-center gap-2 rounded-md border px-3 py-1.5 text-xs font-medium ${
                        benchmarkDrift.tone === 'good'
                          ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                          : benchmarkDrift.tone === 'alert'
                            ? 'border-rose-300 bg-rose-50 text-rose-800'
                            : 'border-amber-300 bg-amber-50 text-amber-800'
                      }`}
                      data-testid="bcb-benchmark-drift-bar"
                      data-drift-tone={benchmarkDrift.tone}
                      data-drift-direction={benchmarkDrift.direction}
                    >
                      <span className="text-[10px] font-bold uppercase tracking-wider opacity-70">
                        Drift
                      </span>
                      <span>{benchmarkDrift.headline}</span>
                    </div>
                  )}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">You</div>
                      <div className="font-semibold text-slate-900">
                        {Number(benchmarkContext.your_value).toFixed(1)}%
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wide text-slate-500">Industry median</div>
                      <div className="font-semibold text-slate-900">
                        {Number(benchmarkContext.benchmark_median).toFixed(1)}%
                      </div>
                    </div>
                    {isStretch ? (
                      <>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500">Top-decile (p90)</div>
                          <div className="font-semibold text-slate-900">
                            {Number(benchmarkContext.benchmark_p90 ?? 0).toFixed(1)}%
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500">Gap to top decile</div>
                          <div className="font-semibold text-teal-700">
                            {Number(benchmarkContext.gap_to_p90 ?? 0).toFixed(1)}pp
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500">Gap</div>
                          <div className={`font-semibold ${benchmarkContext.gap_to_median < 0 ? palette.gapText : 'text-slate-900'}`}>
                            {benchmarkContext.gap_to_median > 0 ? '+' : ''}
                            {Number(benchmarkContext.gap_to_median).toFixed(1)}pp
                          </div>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wide text-slate-500">Band</div>
                          <div className="font-semibold text-slate-900">
                            {benchmarkContext.performance_label}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500 italic">
                    This context is stamped into the Executive Summary and all exports (PDF / Word / PPT).
                  </div>
                </div>
              </div>
              );
            })()}

            {/* Inputs Summary Panel — read-only view of the inputs that drove this case */}
            <CaseInputsPanel
              solutionData={solutionData}
              savedCaseId={savedCaseId}
              title={title}
              onEdit={handleEditInputs}
              isVariant={Boolean(savedCases.find(c => c.id === savedCaseId)?.parent_case_id)}
              onSyncFromParent={handleSyncFromParent}
            />

            {/* Approval & sign-off — only meaningful for a saved case */}
            {savedCaseId && (
              <div className="mb-6">
                <ApprovalPanel
                  caseId={savedCaseId}
                  caseData={approvalData}
                  onChanged={refreshApprovalData}
                />
              </div>
            )}

            {/* Live recalc — edit inputs and re-run ROI without
                bouncing to Solution Finder. Locked once a case is
                submitted or approved to keep the record of record
                stable during review. */}
            {savedCaseId && solutionData && (
              <div className="mb-6">
                <LiveRecalcPanel
                  solutionData={solutionData}
                  modalities={modalitiesCatalog}
                  lockedReason={
                    approvalData?.status === 'approved'
                      ? 'case is approved — unlock by creating a variant'
                      : approvalData?.status === 'submitted'
                        ? 'case is pending approval — wait for the decision or withdraw'
                        : null
                  }
                  onDraftChange={(fresh) => {
                    // Merge the new financial_summary + dampening_factors into
                    // the in-memory solution_data so CaseInputsPanel reflects
                    // the change. We keep the original sections untouched.
                    setSolutionData(prev => prev ? { ...prev, ...fresh } : fresh);
                  }}
                  onPersist={async () => {
                    const token = secureStorage.getItem('session_token');
                    const res = await authedFetch(
                      `${API_URL}/api/business-case-builder/${savedCaseId}`,
                      {
                        method: 'PUT',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                          title,
                          description: `Business case for ${solutionData?.kpi?.name || 'learning investment'}`,
                          sections,
                          solution_data: solutionData,
                        }),
                      }
                    );
                    if (!res.ok) throw new Error('save failed');
                    // Refresh the list so any grid metrics update
                    const orgP = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
                    const listRes = await authedFetch(
                      `${API_URL}/api/business-case-builder/list${orgP}`,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    if (listRes.ok) setSavedCases((await listRes.json()).cases || []);
                  }}
                />
              </div>
            )}

            {/* AI Enhancement Controls */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-4 mb-6">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 bg-purple-100 rounded-lg">
                    <Sparkles className="text-purple-600" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">AI-Powered Enhancement</h3>
                    <p className="text-sm text-gray-600">Generate compelling, persuasive content with GPT-4o</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3">
                  {/* Tone Selector */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-600">Tone:</label>
                    <select
                      value={tone}
                      onChange={(e) => setTone(e.target.value)}
                      className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                    >
                      <option value="professional">Professional</option>
                      <option value="persuasive">Persuasive</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>
                  
                  {/* Generate All Button */}
                  <button
                    onClick={handleGenerateAllWithAI}
                    disabled={generatingAll || !solutionData}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="generate-ai-sections-btn"
                  >
                    {generatingAll ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Sparkles size={18} />
                    )}
                    {generatingAll ? 'Generating...' : 'Regenerate All with AI'}
                  </button>
                </div>
              </div>
              
              {!solutionData && (
                <p className="mt-3 text-sm text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                  ⚠️ AI enhancement requires solution data. Start from the Solution Finder to enable this feature.
                </p>
              )}
            </div>
            
            {/* Title Editor */}
            <div className="bg-white rounded-xl border p-4 mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Business Case Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-4 py-2.5 text-lg font-semibold text-gray-900 border border-gray-200 rounded-lg focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                placeholder="Enter business case title..."
              />
            </div>
            
            {/* Sections */}
            <div className="space-y-4 mb-6">
              {sections.map((section) => (
                <SectionEditor
                  key={section.id}
                  section={section}
                  onChange={handleSectionChange}
                  onToggle={handleToggleSection}
                  onEnhance={handleEnhanceSection}
                  enhancing={enhancing}
                  enhancingId={enhancingId}
                />
              ))}
            </div>
            
            {/* Action Buttons */}
            <div className="bg-white rounded-xl border p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                {/* Save Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handleSave()}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50"
                    data-testid="save-business-case-btn"
                  >
                    {saving ? (
                      <Loader2 className="animate-spin" size={18} />
                    ) : (
                      <Save size={18} />
                    )}
                    {savedCaseId ? 'Update' : 'Save'} Business Case
                  </button>
                  {savedCaseId && (
                    <button
                      onClick={() => handleSave({ asVariant: true })}
                      disabled={saving}
                      className="flex items-center gap-2 px-4 py-2.5 bg-white border border-amber-300 text-amber-800 rounded-lg font-medium hover:bg-amber-50 disabled:opacity-50"
                      title="Save the current edits as a new variant (keeps the original intact)"
                      data-testid="save-as-variant-btn"
                    >
                      <Plus size={16} />
                      Save as variant
                    </button>
                  )}
                </div>
                
                {/* Export Buttons */}
                <div className="flex items-center gap-3 flex-wrap">
                  {familyHasPreferredBookmark && (
                    <label
                      className="flex items-center gap-1.5 text-xs text-slate-600 select-none cursor-pointer"
                      title="Auto-prepends a CFO Recommendation cover slide referencing the bookmarked variant in this family."
                    >
                      <input
                        type="checkbox"
                        checked={includeCfoCover}
                        onChange={(e) => setIncludeCfoCover(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-amber-500 focus:ring-amber-400"
                        data-testid="bcb-include-cfo-cover-checkbox"
                      />
                      <Star size={11} className="text-amber-500" fill="currentColor" />
                      Include CFO Recommendation cover
                    </label>
                  )}
                  <span className="text-sm text-gray-500">Export as:</span>
                  <ExportButton
                    format="pdf"
                    icon={FileText}
                    label="PDF"
                    onClick={handleExport}
                    loading={exporting}
                    loadingFormat={exportFormat}
                  />
                  <ExportButton
                    format="docx"
                    icon={File}
                    label="Word"
                    onClick={handleExport}
                    loading={exporting}
                    loadingFormat={exportFormat}
                  />
                  <ExportButton
                    format="pptx"
                    icon={Presentation}
                    label="PowerPoint"
                    onClick={handleExport}
                    loading={exporting}
                    loadingFormat={exportFormat}
                  />
                </div>
              </div>
            </div>
          </>
        )}
    </div>

    {/* Compare Variants Modal */}
    <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
      <DialogContent className="max-w-[95vw] w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="bcb-compare-dialog">
        <DialogHeader className="px-6 pt-6 pb-2 border-b">
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <GitCompare size={18} className="text-teal-600" />
            Compare Variants
          </DialogTitle>
          {compareData?.parent?.title && (
            <p className="text-xs text-slate-500 mt-1">Parent: <strong className="text-slate-700">{compareData.parent.title}</strong> — side-by-side diff with {compareData.variants?.length || 0} variant{(compareData.variants?.length || 0) === 1 ? '' : 's'}</p>
          )}
        </DialogHeader>
        <div className="flex-1 overflow-auto px-6 py-4">
          {compareLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-teal-600" size={28} />
            </div>
          )}
          {!compareLoading && compareData && (
            <CompareVariantsView
              parent={compareData.parent}
              variants={compareData.variants || []}
              onPromote={handlePromoteVariant}
              promoting={promotingId}
              onTogglePreferred={handleTogglePreferred}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>

    {/* Sync from Parent — diff preview confirm dialog */}
    <Dialog open={syncDialogOpen} onOpenChange={(o) => !syncCommitting && setSyncDialogOpen(o)}>
      <DialogContent className="max-w-lg" data-testid="bcb-sync-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <RefreshCw size={16} className="text-amber-600" />
            Sync from parent
          </DialogTitle>
          {syncPreview?.parent_title && (
            <p className="text-xs text-slate-500 mt-1">From: <strong className="text-slate-700">{syncPreview.parent_title}</strong></p>
          )}
        </DialogHeader>
        <div className="py-2 max-h-[55vh] overflow-auto">
          {!syncPreview && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="animate-spin text-amber-600" size={22} />
            </div>
          )}
          {syncPreview && !syncPreview.has_changes && (
            <div className="text-center py-6">
              <CheckCircle2 className="mx-auto text-emerald-500 mb-2" size={28} />
              <p className="text-sm font-medium text-slate-900">Already up to date</p>
              <p className="text-xs text-slate-500 mt-1">Nothing in this variant differs from the parent — no sync needed.</p>
            </div>
          )}
          {syncPreview?.has_changes && (
            <>
              <p className="text-xs text-slate-600 mb-3">
                The following values will be overwritten with the parent's current data. <strong>Sections and title are preserved.</strong>
              </p>
              <table className="w-full text-xs border-collapse" data-testid="bcb-sync-diff-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                    <th className="p-2 font-semibold">Field</th>
                    <th className="p-2 font-semibold">Current</th>
                    <th className="p-2 font-semibold">→ Parent</th>
                  </tr>
                </thead>
                <tbody>
                  {syncPreview.diffs.map((d) => {
                    const fmt = (v) => {
                      if (v == null) return <span className="italic text-slate-300">—</span>;
                      if (d.format === 'pct') return `${Number(v).toFixed(1)}%`;
                      if (d.format === 'money') return `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
                      return String(v);
                    };
                    return (
                      <tr key={d.label}>
                        <td className="p-2 border-b border-slate-100 font-medium text-slate-700">{d.label}</td>
                        <td className="p-2 border-b border-slate-100 text-slate-500">{fmt(d.current)}</td>
                        <td className="p-2 border-b border-slate-100 font-semibold text-amber-700">{fmt(d.new)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 pt-2 border-t">
          <button
            onClick={() => setSyncDialogOpen(false)}
            disabled={syncCommitting}
            className="px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100 rounded disabled:opacity-50"
            data-testid="bcb-sync-cancel-btn"
          >
            Cancel
          </button>
          {syncPreview?.has_changes && (
            <button
              onClick={handleConfirmSync}
              disabled={syncCommitting}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-600 hover:bg-amber-700 rounded disabled:opacity-50"
              data-testid="bcb-sync-confirm-btn"
            >
              {syncCommitting ? <Loader2 className="animate-spin" size={12} /> : <RefreshCw size={12} />}
              Apply sync
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
    <InlineCreateCaseModal
      open={inlineCreateOpen}
      onClose={() => setInlineCreateOpen(false)}
      onCreated={async (newCaseId) => {
        // Refresh the list so the new case appears, then load it.
        try {
          const token = secureStorage.getItem('session_token');
          const orgP = selectedOrg && selectedOrg !== 'all' ? `?organization_id=${selectedOrg}` : '';
          const r = await authedFetch(`${API_URL}/api/business-case-builder/list${orgP}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (r.ok) setSavedCases((await r.json()).cases || []);
        } catch { /* non-fatal */ }
        if (newCaseId) await handleLoadCase(newCaseId);
      }}
    />
    </PageShell>
  );
};

export default BusinessCaseBuilderPage;
